import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useReport } from '../contexts/ReportContext';
import { useTeam } from '../contexts/TeamContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { PlayerStatsTable } from '../components/reports/PlayerStatsTable';
import { TeamStatsTable } from '../components/reports/TeamStatsTable';
import { LineupsStatsTable } from '../components/reports/LineupsStatsTable';
import { InsightsLineupsStatsTable } from '../components/reports/InsightsLineupsStatsTable';
import { RecommendationsChat } from '../components/chat/RecommendationsChat';
import { ChatMessage } from '../entities/ChatMessage';
import { LoadingAnimation } from '../components/common/LoadingAnimation';
import { AILoadingScreen } from '../components/common/AILoadingScreen';
import { defaultScrollbarStyles } from '../design-patterns/scrollbar';
import { PageContainer } from '../components/common/PageContainer';
import { Dropdown } from '../components/common/Dropdown';
import { DeclineFeedbackModal } from '../components/DeclineFeedbackModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faShield, faExclamationTriangle, faRotateRight, faComments, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  RecommendationCacheEventDetail,
  RECOMMENDATIONS_CACHE_EVENT,
  buildRecommendationCacheKey,
  clearRecommendationCaches as clearGlobalRecommendationCaches,
  getRecommendationCache,
  getRecommendationCacheSnapshot,
  isRecommendationRequestInFlight,
  markRecommendationRequestInFlight,
  clearRecommendationRequestInFlight,
  setRecommendationCache
} from '../utils/recommendationsCache';
import { trackEvent } from '../utils/analytics';

const FullWidthPageContainer = styled(PageContainer)`
  padding: 0 !important;
  gap: 0 !important;
  min-height: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: visible !important;
  box-sizing: border-box !important;
`;

const ReportsContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: visible;
  background: transparent;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: 0 var(--spacing-2) var(--spacing-6) rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(0.75rem);
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    border-radius: var(--radius-lg);
  }
`;

const MainContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  flex: 1;
  min-height: 0;
  padding: clamp(var(--spacing-4), 2vw, var(--spacing-6));
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  box-sizing: border-box;
  overflow-y: visible;
  overflow-x: auto;
  background: transparent;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: var(--spacing-5);
    gap: var(--spacing-5);
  }

  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-4);
    gap: var(--spacing-4);
  }
`;

const TitleSection = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  padding: clamp(var(--spacing-3), 1.5vw, var(--spacing-4)) clamp(var(--spacing-5), 2.5vw, var(--spacing-6));
  background: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border);
  box-shadow: 0 var(--spacing-8) var(--spacing-16) rgba(0, 0, 0, 0.5), 0 0 0 0.0625rem rgba(255, 77, 77, 0.15);
  backdrop-filter: blur(1.5rem);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 0%, rgba(255, 77, 77, 0.2), transparent 70%);
    pointer-events: none;
  }
`;

const TitleInfo = styled.div`
  flex: 1;
`;

const MainTitle = styled.h1`
  font-family: var(--font-family-sans);
  font-size: clamp(1.1rem, 1.8vw, 1.25rem);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  letter-spacing: -0.02em;
`;

// Primary red action button used across the app
const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: clamp(var(--spacing-2), 2vw, var(--spacing-3)) clamp(var(--spacing-4), 3vw, var(--spacing-5));
  min-height: 2.75rem; /* 44px - accessibility standard for touch targets */
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
  color: white;
  border: none;
  border-radius: var(--radius-pill);
  cursor: pointer;
  font-size: clamp(var(--font-size-xs), 2vw, var(--font-size-sm));
  font-family: var(--font-family-sans);
  font-weight: 600;
  transition: all 0.3s var(--transition-smooth);
  width: 100%;
  box-shadow: 0 var(--spacing-1) var(--spacing-3) rgba(255, 43, 43, 0.3);
  outline: none;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent; /* Remove tap highlight on mobile */

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.2), transparent 70%);
    opacity: 0;
    transition: opacity 0.3s var(--transition-smooth);
  }

  &:hover {
    background: linear-gradient(135deg, var(--color-accent-light), var(--color-accent));
    box-shadow: 0 var(--spacing-2) var(--spacing-4) rgba(232, 19, 19, 0.5);
    transform: translateY(-0.0625rem);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: scale(0.98); /* Touch feedback */
  }

  &:focus-visible {
    outline: 0.125rem solid var(--color-accent);
    outline-offset: 0.125rem;
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    min-height: 3rem; /* 48px - larger for easier tapping */
    font-size: var(--font-size-sm);
  }
`;






const ErrorMessage = styled.div`
  position: relative;
  z-index: 1;
  color: var(--color-text-primary);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.1));
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: var(--radius-xl);
  padding: var(--spacing-5) var(--spacing-6);
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  box-shadow: 0 var(--spacing-2) var(--spacing-6) rgba(239, 68, 68, 0.2);
  backdrop-filter: blur(0.75rem);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 50%, rgba(239, 68, 68, 0.2), transparent 60%);
    border-radius: inherit;
    pointer-events: none;
  }
`;

const ReportTabs = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  background: var(--color-surface-alt);
  border-radius: var(--radius-xl);
  padding: var(--spacing-1);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-inset);
  backdrop-filter: blur(1.25rem);
  flex-shrink: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  justify-content: space-between;
  gap: var(--spacing-1);
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    flex-wrap: nowrap;
    padding: 0.1875rem; /* 3px in rem */
    gap: 0.1875rem; /* 3px in rem */
  }
`;

const ReportTab = styled.button<{ $isActive: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3) var(--spacing-5);
  background: transparent;
  color: ${props => props.$isActive ? '#ffffff' : 'var(--color-text-secondary)'};
  border: none !important;
  border-radius: var(--radius-xl);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: color 0.4s ease-in-out, transform 0.4s ease-in-out;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  outline: none !important;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
    opacity: ${props => props.$isActive ? 1 : 0};
    transition: opacity 0.4s ease-in-out;
    pointer-events: none;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${props => props.$isActive 
      ? 'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.2), transparent 70%)'
      : 'transparent'};
    opacity: ${props => props.$isActive ? 1 : 0};
    transition: opacity 0.4s ease-in-out;
    pointer-events: none;
    z-index: -1;
  }


  .desktop-text {
    display: block;
  }
  
  .mobile-text {
    display: none;
  }

  &:hover {
    &::before {
      background: ${props => props.$isActive 
        ? 'linear-gradient(135deg, var(--color-accent-light), var(--color-accent))'
        : 'transparent'};
      opacity: ${props => props.$isActive ? 1 : 0};
    }
    color: ${props => props.$isActive ? '#ffffff' : 'var(--color-text-primary)'};
    transform: ${props => props.$isActive ? 'translateY(-0.0625rem)' : 'none'};
  }

  &:focus,
  &:focus-visible,
  &:active,
  &:focus-within {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }

  @media (max-width: 48rem) { /* --breakpoint-tablet */
    flex: 1;
    min-width: 0;
    padding: var(--spacing-3) var(--spacing-3);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    
    .desktop-text {
      display: none;
    }
    
    .mobile-text {
      display: block;
    }
  }

  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-2) var(--spacing-2);
    font-size: var(--font-size-xs);
  }
`;



const KeyInsightsSection = styled.div`
  margin-bottom: var(--spacing-3);
  flex-shrink: 0;
  background: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-6);
  padding-top: var(--spacing-8); /* Extra top padding to prevent hover overlap */
  padding-bottom: var(--spacing-6); /* Reduced bottom padding for better border visibility */
  box-shadow: 0 var(--spacing-2) var(--spacing-6) rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(1.25rem);
  overflow: hidden; /* Clip content to show proper borders */
  
  @media (max-width: 30rem) {
    padding-bottom: var(--spacing-4); /* Reduced padding on mobile for better border visibility */
  }
`;

const KeyInsightsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-3);
  
  @media (max-width: 30rem) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-2);
  }
`;

const KeyInsightsTitle = styled.h2`
  font-family: var(--font-family-sans);
  font-size: clamp(var(--font-size-base), 1.8vw, var(--font-size-md));
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  white-space: normal; /* Allow text to wrap */
  overflow: visible; /* Show all text */
  text-overflow: unset; /* Don't truncate */
  flex: 1;

  &::before {
    content: '';
    width: 0.1875rem; /* 3px in rem */
    height: 1.125rem; /* 18px in rem */
    background: linear-gradient(180deg, var(--color-accent), var(--color-accent-light));
    border-radius: var(--spacing-1);
    flex-shrink: 0;
  }
`;

const InsightsRefreshButton = styled.button`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  font-family: var(--font-family-sans);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: var(--color-surface-alt);
    border-color: var(--color-accent);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const MetricsScrollContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: var(--spacing-2);
  overflow: hidden; /* Clip content to show proper borders */
  overflow-y: hidden; /* Prevent vertical overflow - cards should stay within bounds */
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* Align content to top */
  min-height: 22rem; /* Increased to accommodate taller cards */
  padding-top: var(--spacing-4); /* Increased top padding to move content down further */
  padding-bottom: var(--spacing-4); /* Add bottom padding for border visibility */
  box-sizing: border-box; /* Ensure padding is included in height calculation */
  
  @media (max-width: 64rem) { /* --breakpoint-desktop */
    min-height: 20rem;
    padding-top: var(--spacing-10);
    padding-bottom: var(--spacing-5);
  }
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    min-height: 18rem;
    padding-top: var(--spacing-8);
    padding-bottom: var(--spacing-4);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding-bottom: var(--spacing-0);
    height: 30rem;
  }
`;

const MetricsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: var(--spacing-3);
  overflow-x: auto;
  overflow-y: hidden; /* Prevent vertical overflow */
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scroll-padding: 0;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  height: 23rem; /* Fixed height to match card height exactly */
  align-items: flex-start; /* Align cards at top for consistent appearance */
  margin-top: var(--spacing-0); /* Increased margin to push content down further */
  margin-bottom: 0; /* Remove bottom margin to maximize vertical space */
  flex: 0 0 auto; /* Don't grow or shrink - fixed size */
  max-width: 100%; /* Prevent overflow beyond parent */
  box-sizing: border-box; /* Ensure proper sizing */
  position: relative; /* Ensure proper positioning context */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  padding-top: 0.5rem; /* Add this - gives space for hover effect */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  /* Hide scrollbar but keep functionality */
  & {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  
  /* Ensure cards don't wrap */
  > * {
    flex-shrink: 0;
  }
  
  @media (max-width: 64rem) { /* --breakpoint-desktop */
    height: 20rem; /* Fixed height */
    margin-top: var(--spacing-3);
  }
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    height: 18rem; /* Fixed height */
    margin-top: var(--spacing-2);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    overflow-x: auto; /* Allow scrolling on mobile */
    overflow-y: hidden;
    /* Ensure only one card is visible at a time - clip to container width */
    scroll-snap-type: x mandatory;
    height: 24rem;
  }
`;

const MetricCard = styled.div<{ $isPageStart?: boolean }>`
  position: relative;
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-4);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: none;
  backdrop-filter: none;
  transition: transform 0.3s var(--transition-smooth), border-color 0.3s var(--transition-smooth);
  overflow: hidden; /* Hide overflow to maintain fixed dimensions */
  z-index: 50; /* Ensure cards appear above tables */
  /* Make card square: width matches height */
  width: 22rem;
  height: 22rem;
  flex-shrink: 0;
  scroll-snap-align: ${props => props.$isPageStart ? 'start' : 'none'};
  justify-content: flex-start; /* Start from top to show all content */
  box-sizing: border-box;
  gap: var(--spacing-2);
  
  @media (max-width: 64rem) { /* --breakpoint-desktop */
    width: 20rem;
    height: 20rem;
  }
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    width: 18rem;
    height: 18rem;
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile - must be last to override */
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    scroll-snap-align: start;
    aspect-ratio: 1; /* Keep square on mobile */
  }

  &:hover {
    transform: translateY(-0.125rem);
    border-color: var(--color-accent);
    border-width: 1px; /* Make border more visible on hover */
    position: relative;
    z-index: 60; /* Ensure hovered card appears above other cards and tables */
    box-shadow: 0 var(--spacing-2) var(--spacing-4) rgba(var(--color-accent-rgb), 0.3); /* Add shadow to make hover more visible */
  }
`;

const MetricValue = styled.div<{ $isPositive?: boolean; $isNegative?: boolean }>`
  font-family: var(--font-family-sans);
  font-size: clamp(var(--font-size-lg), 1.5vw, var(--font-size-xl));
  font-weight: 700;
  font-stretch: normal;
  letter-spacing: normal;
  color: ${props => 
    props.$isPositive ? 'var(--color-success)' : 
    props.$isNegative ? 'var(--color-error)' : 
    'var(--color-accent-light)'
  };
  margin-bottom: var(--spacing-1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  text-shadow: 0 0.125rem var(--spacing-2) rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
`;

const MetricLabel = styled.div`
  font-family: var(--font-family-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 500;
  line-height: var(--line-height-normal);
  overflow: hidden; /* Hide overflow to maintain fixed dimensions */
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 6; /* Limit to 6 lines to leave room for bottom section */
  -webkit-box-orient: vertical;
  width: 100%;
  flex-shrink: 1;
  min-height: 0;
  word-wrap: break-word; /* Allow long text to wrap */
  overflow-wrap: break-word; /* Additional wrapping support */
  margin-top: var(--spacing-3);
  margin-bottom: var(--spacing-1);
`;

const MetricStat = styled.div`
  font-family: var(--font-family-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  font-weight: 700;
  margin-bottom: var(--spacing-2);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-stretch: normal;
  opacity: 0.9;
  flex-shrink: 0;
`;

const MetricRank = styled.div`
  font-family: var(--font-family-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 500;
  font-stretch: normal;
  letter-spacing: normal;
  margin-bottom: var(--spacing-2);
  flex-shrink: 0;
`;

const MetricBottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: var(--spacing-3);
  gap: var(--spacing-1);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  flex: 1;
`;

const MetricBottomItem = styled.div`
  font-family: var(--font-family-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 400;
  text-align: center;
`;

const MetricName = styled.div`
  font-family: var(--font-family-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-primary);
  font-weight: 500;
  margin-bottom: var(--spacing-1);
  text-align: center;
  flex-shrink: 0;
`;

const CopyButton = styled.button`
  position: absolute;
  top: var(--spacing-12);
  right: var(--spacing-2);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s var(--transition-smooth);
  opacity: 0.7;
  padding: 0;
  outline: none;
  z-index: 10;
  
  &:hover {
    opacity: 1;
    background: var(--color-surface-alt);
    border-color: var(--color-accent);
    color: var(--color-accent);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus,
  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
  
  svg {
    font-size: var(--font-size-sm);
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto; /* Push to bottom of container */
  padding: 0.75rem 0 0.25rem 0;
  flex-shrink: 0; /* Don't shrink */
  position: relative;
`;

const ArrowButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none !important;
  background: transparent;
  color: var(--color-text-primary);
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: transform 0.2s var(--transition-smooth);
  opacity: ${props => props.$disabled ? 0.4 : 1};
  font-size: 1.2rem;
  font-weight: 600;
  padding: 0;
  outline: none !important;
  box-shadow: none !important;
  
  &:hover:not(:disabled) {
    transform: scale(1.2);
    outline: none !important;
    box-shadow: none !important;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.9);
    transition: transform 0.1s ease-out;
    outline: none !important;
    box-shadow: none !important;
  }
  
  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }
  
  &:disabled {
    cursor: not-allowed;
    outline: none !important;
    box-shadow: none !important;
  }
`;

const Dot = styled.div<{ $isActive: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: ${props => props.$isActive 
    ? '2px solid var(--color-accent)' 
    : '2px solid transparent'};
  background: ${props => props.$isActive 
    ? 'var(--color-accent)' 
    : 'var(--color-border)'};
  opacity: ${props => props.$isActive ? 1 : 0.4};
  /* Match scroll animation duration - both dots animate simultaneously */
  transition: opacity 0.4s ease-in-out, background 0.4s ease-in-out, border-color 0.4s ease-in-out;
  pointer-events: none; /* Make dots unclickable */
  box-sizing: border-box;
`;


const SecondaryMetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.8rem;
  margin-bottom: 0.8rem;
`;

const SecondaryMetricCard = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const MetricIcon = styled.div`
  font-size: 1.5rem;
  color: var(--color-accent);
`;

const SecondaryMetricInfo = styled.div`
  flex: 1;
`;

const SecondaryMetricValue = styled.div`
  font-family: var(--font-family-sans);
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 0.2rem;
`;

const SecondaryMetricLabel = styled.div`
  font-family: var(--font-family-sans);
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  font-weight: 500;
`;

const OpponentStatsSection = styled.div`
  margin-top: 0.5rem;
  flex: 0 1 auto;
  max-height: 50vh;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;

  @media (max-width: 768px) {
    margin-top: 0.3rem;
    max-height: 45vh;
  }
`;

const StatsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StatsTitle = styled.h2`
  font-family: var(--font-family-sans);
  font-size: clamp(1.2rem, 2.5vw, 1.5rem);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before {
    content: '';
    width: 4px;
    height: 24px;
    background: linear-gradient(180deg, var(--color-accent), var(--color-accent-light));
    border-radius: 2px;
  }
  
  @media (max-width: 30rem) {
    font-size: clamp(0.9rem, 2vw, 1.2rem);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    gap: 0.5rem;
    
    &::before {
      width: 3px;
      height: 18px;
    }
  }
`;

const StatsToggle = styled.div`
  display: flex;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(64, 64, 64, 0.08));
  border-radius: 1.5rem;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(12px);
  gap: 3px;
`;

const ToggleButton = styled.button<{ $isActive: boolean }>`
  position: relative;
  padding: 0.65rem 1.4rem;
  background: ${props => props.$isActive 
    ? 'linear-gradient(135deg, var(--color-accent), var(--color-accent-light))' 
    : 'transparent'};
  color: ${props => props.$isActive ? '#ffffff' : 'var(--color-text-secondary)'};
  border: none;
  border-radius: 1.25rem;
  font-family: var(--font-family-sans);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s var(--transition-smooth);
  white-space: nowrap;
  min-width: 90px;
  outline: none;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${props => props.$isActive 
      ? 'radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.2), transparent 70%)'
      : 'transparent'};
    opacity: ${props => props.$isActive ? 1 : 0};
    transition: opacity 0.3s var(--transition-smooth);
    pointer-events: none;
  }

  ${props => props.$isActive && `
    box-shadow: 0 4px 12px rgba(255, 43, 43, 0.3);
  `}

  &:hover {
    background: ${props => props.$isActive 
      ? 'linear-gradient(135deg, var(--color-accent-light), var(--color-accent))'
      : 'rgba(255, 255, 255, 0.08)'};
    color: ${props => props.$isActive ? '#ffffff' : 'var(--color-text-primary)'};
    transform: ${props => props.$isActive ? 'translateY(-1px)' : 'none'};
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    padding: 0.55rem 1.1rem;
    font-size: 0.85rem;
    min-width: 75px;
  }
`;

const StatsTableContainer = styled.div`
  position: relative;
  overflow: visible;
  border-radius: 1.5rem;
  border: 1px solid var(--color-border);
  background: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px);
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;

  /* Ensure table headers stay above content */
  table {
    position: relative;
    z-index: 1;
  }

  thead {
    position: sticky;
    top: 0;
  }

  /* Ensure proper horizontal scrolling */
  tbody {
    position: relative;
    z-index: 0; /* Lower z-index so body stays behind header */
  }
`;

const StatsTable = styled.table`
  width: 100%;
  background: var(--color-background);
  border-collapse: collapse;
  table-layout: auto;
`;

const StatsTableHeader = styled.thead`
  background: var(--color-background);
  position: sticky;
  top: 0;
  z-index: 20; /* Ensure thead stays above tbody */
`;

const StatsTableRow = styled.tr`
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }
`;

const StatsTableHeaderCell = styled.th<{ $isFixed?: boolean }>`
  padding: 0.4rem 0.3rem;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  background: var(--color-background);
  border-bottom: 2px solid var(--color-border-strong);
  position: sticky;
  top: 0;

  ${props => props.$isFixed && `
    left: 0;
    background: var(--color-background);
    z-index: 30;
    border-right: 2px solid var(--color-border-strong);
    width: 10rem; /* 160px in rem */
    min-width: 10rem;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
    
    @media (max-width: 48rem) { /* --breakpoint-tablet */
      width: 9.375rem; /* 150px in rem */
      min-width: 9.375rem;
    }
    
    @media (max-width: 30rem) { /* --breakpoint-mobile */
      width: 8rem; /* 128px in rem */
      min-width: 8rem;
      font-size: 0.7rem;
    }
  `}

  ${props => !props.$isFixed && `
    z-index: 26; /* Above all body cells (25 and below) */
    min-width: 3.75rem; /* 60px in rem */
    text-align: center;
    
    @media (max-width: 48rem) { /* --breakpoint-tablet */
      min-width: 2.5rem; /* 40px in rem */
    }
    
    @media (max-width: 30rem) { /* --breakpoint-mobile */
      min-width: 2rem; /* 32px in rem */
      font-size: 0.7rem;
    }
  `}
`;

const StatsTableCell = styled.td<{ $isFixed?: boolean }>`
  padding: 0.4rem 0.3rem;
  font-size: 0.8rem;
  color: var(--color-text-primary);
  font-weight: 500;
  white-space: nowrap;
  text-align: left;

  ${props => props.$isFixed && `
    position: sticky;
    left: 0;
    background: var(--color-background);
    z-index: 25;
    border-right: 1px solid var(--color-border);
    width: 10rem; /* 160px in rem */
    min-width: 10rem;
    font-weight: 600;
    
    @media (max-width: 48rem) { /* --breakpoint-tablet */
      width: 9.375rem; /* 150px in rem */
      min-width: 9.375rem;
    }
    
    @media (max-width: 30rem) { /* --breakpoint-mobile */
      width: 8rem; /* 128px in rem */
      min-width: 8rem;
      font-size: 0.7rem;
    }
  `}

  ${props => !props.$isFixed && `
    z-index: 1; /* Below all header cells */
    min-width: 3.75rem; /* 60px in rem */
    text-align: center;
    
    @media (max-width: 48rem) { /* --breakpoint-tablet */
      min-width: 2.5rem; /* 40px in rem */
    }
    
    @media (max-width: 30rem) { /* --breakpoint-mobile */
      min-width: 2rem; /* 32px in rem */
      font-size: 0.7rem;
    }
  `}
`;

const RowLabel = styled.span<{ $type: 'wins' | 'losses' | 'total' }>`
  color: ${props => 
    props.$type === 'wins' ? 'var(--color-success)' : 
    props.$type === 'losses' ? 'var(--color-error)' : 
    'var(--color-text-primary)'
  };
  font-weight: 600;
`;

const RecommendationCard = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.2rem;
  display: flex;
  gap: 0.8rem;
  flex: 1;
`;

const RecommendationIcon = styled.div`
  font-size: 1.5rem;
  color: var(--color-accent);
  flex-shrink: 0;
`;

const RecommendationContentOld = styled.div`
  flex: 1;
`;

const RecommendationTitleOld = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.4rem;
`;

const RecommendationLineup = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-accent);
  margin-bottom: 0.4rem;
  line-height: 1.4;
`;

const RecommendationDescription = styled.div`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.8rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;

`;

const StrategicInsightsCard = styled.div`
  background: var(--color-surface);
  border-radius: 16px;
  border: 1px solid var(--color-border);
  padding: 1.5rem;
  backdrop-filter: blur(20px);
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
`;

const TeamSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const TeamSectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: ${props => props.color === 'green' ? '"🛡"' : '"🎯"'};
    color: ${props => props.color === 'green' ? 'var(--color-success)' : 'var(--color-error)'};
  }
`;

const StrengthsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const StrengthItem = styled.li`
  color: white;
  font-size: 1rem;
  line-height: 1.4;
  padding-left: 1.5rem;
  position: relative;

  &::before {
    content: "•";
    position: absolute;
    left: 0;
    color: var(--color-accent);
    font-weight: bold;
  }
`;

// Collapsible Recommendation Component Styles
const CollapsibleRecommendationCard = styled.div`
  background: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  border: 1px solid var(--color-border);
  border-radius: 1.5rem;
  margin-bottom: 1rem;
  overflow: hidden;
  transition: all 0.4s ease-in-out;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);

  &:hover {
    background: linear-gradient(140deg, var(--color-surface-alt), var(--color-elevated));
    border-color: rgba(255, 77, 77, 0.5);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 0 0 1px rgba(255, 77, 77, 0.1);
    transform: translateY(-1px);
  }
  
  [data-theme='light'] & {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(0, 0, 0, 0.06);
    
    &:hover {
      background: linear-gradient(140deg, var(--color-surface-alt), var(--color-elevated));
      border-color: rgba(255, 77, 77, 0.5);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 77, 77, 0.15);
      transform: translateY(-1px);
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const RecommendationHeader = styled.div`
  padding: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const RecommendationTitle = styled.div`
  font-family: var(--font-family-sans);
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  flex: 1;
  line-height: 1.4;
`;

const ExpandIcon = styled.div<{ isExpanded: boolean }>`
  font-size: 0.75rem;
  color: var(--color-accent);
  transition: transform 0.4s ease-in-out;
  transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-left: auto;
  
  &::before {
    content: '▼';
    display: block;
  }
`;

const CollapsibleRecommendationContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const RefreshIconWrapper = styled.div<{ $isSpinning: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: ${props => props.$isSpinning ? 'spin 1s linear infinite' : 'none'};
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const RecommendationRefreshButton = styled.button`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  font-family: var(--font-family-sans);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s var(--transition-smooth);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 2px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(12px);
  
  &:hover:not(:disabled) {
    background: var(--color-surface-alt);
    border-color: var(--color-accent);
    color: var(--color-accent);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 4px 12px rgba(0, 0, 0, 0.3);
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const RecommendationDetails = styled.div`
  padding: 0 1.2rem 1.2rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-accent);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailText = styled.div`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ShowMoreButton = styled.button`
  background-color: transparent;
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: 8px;
  padding: 0.8rem 1.2rem;
  font-size: 0.9rem;
  font-family: var(--font-family-sans);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  transition: all 0.3s var(--transition-smooth);

  &:hover {
    background-color: var(--color-accent);
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
`;

const ApproveButton = styled.button<{ $isLoading?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.25rem;
  background: linear-gradient(135deg, var(--color-success), rgba(74, 222, 128, 0.8));
  color: white;
  border: none;
  border-radius: var(--radius-pill);
  cursor: ${props => props.$isLoading ? 'not-allowed' : 'pointer'};
  font-size: 0.85rem;
  font-family: var(--font-family-sans);
  font-weight: 600;
  transition: all 0.3s var(--transition-smooth);
  box-shadow: 0 4px 12px rgba(74, 222, 128, 0.3);
  outline: none;
  position: relative;
  overflow: hidden;
  gap: 0.5rem;
  opacity: ${props => props.$isLoading ? 0.8 : 1};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.2), transparent 70%);
    opacity: 0;
    transition: opacity 0.3s var(--transition-smooth);
  }

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(74, 222, 128, 0.9), var(--color-success));
    box-shadow: 0 6px 16px rgba(74, 222, 128, 0.4);
    transform: translateY(-1px);

    &::before {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const RejectButton = styled.button<{ $isLoading?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.25rem;
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
  color: #ffffff;
  border: none;
  border-radius: var(--radius-pill);
  cursor: ${props => props.$isLoading ? 'not-allowed' : 'pointer'};
  font-size: 0.85rem;
  font-family: var(--font-family-sans);
  font-weight: 600;
  transition: all 0.3s var(--transition-smooth);
  box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.3);
  outline: none;
  position: relative;
  overflow: hidden;
  gap: 0.5rem;
  opacity: ${props => props.$isLoading ? 0.8 : 1};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.2), transparent 70%);
    opacity: 0;
    transition: opacity 0.3s var(--transition-smooth);
  }

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--color-accent-light), var(--color-accent));
    box-shadow: 0 6px 16px rgba(var(--color-accent-rgb), 0.5);
    transform: translateY(-1px);

    &::before {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

// Collapsible Recommendation Component
interface CollapsibleRecommendationProps {
  recommendation: {
    id: string;
    focus_area: string;
    supporting_data: string;
    integration_with_our_team: string;
    coaching_suggestion: string;
  };
  index: number;
  onApprove?: (recommendationId: string) => void;
  onDecline?: (recommendationId: string) => void;
  isLoading?: boolean;
  isApproving?: boolean;
}

const CollapsibleRecommendation: React.FC<CollapsibleRecommendationProps> = ({ recommendation, index, onApprove, onDecline, isLoading, isApproving }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = (e: React.MouseEvent) => {
    // Don't expand/collapse if clicking on action buttons!
    if ((e.target as HTMLElement).closest('.action-buttons')) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onApprove) {
      onApprove(recommendation.id);
    }
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDecline) {
      onDecline(recommendation.id);
    }
  };

  return (
    <CollapsibleRecommendationCard onClick={toggleExpanded}>
      <RecommendationHeader>
        <RecommendationTitle>
          {recommendation.focus_area}
        </RecommendationTitle>
        <ExpandIcon isExpanded={isExpanded} />
      </RecommendationHeader>
      
      <CollapsibleRecommendationContent isExpanded={isExpanded}>
        <RecommendationDetails>
          <DetailSection>
            <DetailLabel>Coaching Suggestion</DetailLabel>
            <DetailText>{recommendation.coaching_suggestion}</DetailText>
          </DetailSection>
          
          <DetailSection>
            <DetailLabel>Supporting Data</DetailLabel>
            <DetailText>{recommendation.supporting_data}</DetailText>
          </DetailSection>
          
          {/* <DetailSection>
            <DetailLabel>Integration With Our Team</DetailLabel>
            <DetailText>{recommendation.integration_with_our_team}</DetailText>
          </DetailSection> */}
          
          <DetailSection>
            <ActionButtonsContainer>
              <ApproveButton 
                onClick={handleApprove}
                disabled={isLoading || isApproving}
                $isLoading={isApproving}
              >
                {isApproving ? (
                  <>
                    <LoadingSpinner />
                    Updated
                  </>
                ) : (
                  '✓ Approve'
                )}
              </ApproveButton>
              <RejectButton 
                onClick={handleReject}
                disabled={isLoading || isApproving}
                $isLoading={isLoading}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner />
                    Loading...
                  </>
                ) : (
                  '✗ Reject'
                )}
              </RejectButton>
            </ActionButtonsContainer>
          </DetailSection>
        </RecommendationDetails>
      </CollapsibleRecommendationContent>
    </CollapsibleRecommendationCard>
  );
};

const WeaknessesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const WeaknessItem = styled.li`
  color: white;
  font-size: 1rem;
  line-height: 1.4;
  padding-left: 1.5rem;
  position: relative;

  &::before {
    content: "•";
    position: absolute;
    left: 0;
    color: #EF4444;
    font-weight: bold;
  }
`;



const ScrollableContent = styled.div`
  flex: 1;
  overflow: visible;
  min-height: 0;
  max-height: 100%;
  padding-right: 0.5rem;
  display: flex;
  flex-direction: column;
`;

// New styled components for Insights tab controls
const InsightsControls = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  padding-left: 1rem; /* Match KeyInsightsSection inline padding */
  
  @media (max-width: 48rem) {
    gap: 0.75rem;
    padding-left: 0.75rem;
  }
  
  @media (max-width: 30rem) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ControlsLeft = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: nowrap;
  align-items: flex-start;
  
  @media (max-width: 48rem) {
    gap: 0.75rem;
    width: 100%;
  }
  
  @media (max-width: 30rem) {
    flex-direction: column;
    width: 100%;
    gap: 0.5rem;
  }
`;

const ControlsRight = styled.div`
  display: flex;
  align-items: flex-end;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  flex: 0 1 auto;
  min-width: 0;
  
  /* Ensure all ControlGroups have the same label height for alignment */
  & > label {
    min-height: 1.5rem;
    display: flex;
    align-items: flex-end;
    width: 100%;
  }
  
  /* Ensure dropdown and other inputs align with label */
  & > *:not(label) {
    width: 100%;
    min-width: 0;
  }
  
  /* First ControlGroup (Stats Type) - fixed width */
  &:first-child {
    flex: 0 0 auto;
    min-width: 140px;
  }
  
  /* Second ControlGroup (Splits) - flexible width */
  &:nth-child(2) {
    flex: 1 1 auto;
    min-width: 200px;
  }
  
  @media (max-width: 48rem) {
    &:first-child {
      min-width: 120px;
    }
    
    &:nth-child(2) {
      min-width: 160px;
    }
  }
  
  @media (max-width: 30rem) {
    width: 100%;
    
    &:first-child {
      min-width: unset;
      width: 100%;
    }
    
    &:nth-child(2) {
      min-width: unset;
      width: 100%;
    }
  }
`;

const ControlLabel = styled.label`
  font-family: var(--font-family-sans);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  letter-spacing: 0.02em;
  height: 1.5rem;
  display: flex;
  align-items: flex-end;
  margin: 0;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
`;

const SplitsMultiSelect = styled.div`
  position: relative;
  min-width: 200px;
  width: 100%;
  box-sizing: border-box;
`;

const SplitsDropdownContainer = styled.div`
  position: relative;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 1rem;
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.3s var(--transition-smooth);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(12px);
  
  &:hover {
    border-color: rgba(255, 77, 77, 0.4);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 6px 16px rgba(0, 0, 0, 0.3);
  }
  
  &:focus-within {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px rgba(255, 43, 43, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
`;
  
const ChatTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
  min-height: 500px;
  padding: 1rem;
  background: transparent;
  border-radius: 1.5rem;
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--color-surface);
  border-radius: 1rem;
  border: 1px solid var(--color-border);
`;

const ChatHeaderTitle = styled.div`
  color: var(--color-text-primary);
  font-size: 1.2rem;
  font-weight: 600;
  font-family: var(--font-family-sans);
`;

const ClearChatButton = styled.button`
  background: var(--color-surface-alt);
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  color: var(--color-text-primary);
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s var(--transition-smooth);
  
  &:hover {
    background: var(--color-surface);
    border-color: var(--color-border-strong);
  }
`;

const ChatMessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: transparent;
  border-radius: 1rem;
  border: none;
  
  /* Custom scrollbar - pill shape, no arrows */
  &::-webkit-scrollbar {
    width: 8px;
    -webkit-appearance: none;
  }
  
  &::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 77, 77, 0.5);
    border-radius: 999px; /* Pill shape */
    min-height: 40px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 77, 77, 0.7);
  }
  
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 77, 77, 0.5) rgba(255, 255, 255, 0.05);
`;

const ChatEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-text-secondary);
  text-align: center;
  padding: 2rem;
  
  > div:first-child {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  > div:last-child {
    font-size: 0.9rem;
  }
`;

const ChatMessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 80%;
  padding: 0.75rem 1rem;
  border-radius: ${props => props.$isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px'};
  background: ${props => props.$isUser 
    ? 'linear-gradient(135deg, var(--color-accent), var(--color-accent-light))' 
    : 'var(--color-surface)'};
  color: ${props => props.$isUser ? '#ffffff' : 'var(--color-text-primary)'};
  font-size: 0.95rem;
  line-height: 1.4;
  margin-bottom: 1rem;
  border: ${props => props.$isUser ? 'none' : '1px solid var(--color-border)'};
`;

const ChatInputForm = styled.form`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--color-surface);
  border-radius: 1rem;
  border: 1px solid var(--color-border);
`;

const ChatInputField = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  background: var(--color-background-alt);
  border: 1px solid var(--color-border);
  border-radius: 0.75rem;
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-family: var(--font-family-sans);
  outline: none;
  transition: all 0.2s var(--transition-smooth);
  
  &:focus {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-secondary);
  }
`;

const ChatSendButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
  color: #ffffff;
  border: none;
  border-radius: 0.75rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s var(--transition-smooth);
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SplitsPlaceholder = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  flex: 1;
  min-width: 120px;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    color: #d0d0d0;
  }
`;

const SelectedSplitsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1;
  align-items: center;
  
  @media (max-width: 30rem) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const SplitChip = styled.div`
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
  color: white;
  padding: 0.4rem 0.75rem;
  border-radius: var(--radius-pill);
  font-size: 0.85rem;
  font-weight: 600;
  font-family: var(--font-family-sans);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(255, 43, 43, 0.3);
  transition: transform 0.2s var(--transition-smooth), box-shadow 0.2s var(--transition-smooth);
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 43, 43, 0.4);
  }
  
  @media (max-width: 30rem) {
    padding: 0.3rem 0.5rem;
    font-size: 0.75rem;
    gap: 0.35rem;
  }
`;

const RemoveSplitButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  transition: all 0.2s var(--transition-smooth);
  line-height: 1;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const DropdownIcon = styled.div<{ $isOpen: boolean }>`
  color: var(--color-text-secondary);
  font-size: 0.75rem;
  margin-left: 0.75rem;
  transition: transform 0.3s var(--transition-smooth);
  display: flex;
  align-items: center;
  
  ${props => props.$isOpen && `
    transform: rotate(180deg);
  `}
`;

const DropdownList = styled.div<{ $isOpen: boolean; $openUpward?: boolean }>`
  position: absolute;
  ${props => props.$openUpward 
    ? `bottom: calc(100% + 4px);` 
    : `top: calc(100% + 4px);`}
  left: 0;
  right: 0;
  background: linear-gradient(140deg, var(--color-surface), var(--color-surface-alt));
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 77, 77, 0.1);
  backdrop-filter: blur(24px);
  ${defaultScrollbarStyles}
`;

const DropdownOption = styled.div<{ $isSelected: boolean }>`
  padding: 0.75rem 1.25rem;
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  cursor: pointer;
  font-size: 0.9rem;
  font-family: var(--font-family-sans);
  font-weight: ${props => props.$isSelected ? '600' : '500'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s var(--transition-smooth);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, var(--color-accent), var(--color-accent-light));
    opacity: ${props => props.$isSelected ? 1 : 0};
    transition: opacity 0.2s var(--transition-smooth);
    border-radius: 0 2px 2px 0;
  }
  
  &:hover {
    background: ${props => props.$isSelected 
      ? 'linear-gradient(90deg, rgba(255, 77, 77, 0.1), transparent)' 
      : 'rgba(255, 255, 255, 0.08)'};
    color: var(--color-text-primary);
  }
  
  ${props => props.$isSelected && `
    background: linear-gradient(90deg, rgba(255, 77, 77, 0.08), transparent);
  `}
  
  &:first-child {
    border-radius: 1rem 1rem 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 1rem 1rem;
  }
`;

const ClearAllButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 700;
  padding: 0;
  margin-left: 0.5rem;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s var(--transition-smooth);
  line-height: 1;
  
  &:hover {
    background: rgba(var(--color-error-rgb), 0.2);
    border-color: rgba(var(--color-error-rgb), 0.4);
    color: var(--color-error);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const RefreshAllButton = styled.button`
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-light));
  color: white;
  border: none;
  border-radius: 1rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.9rem;
  font-family: var(--font-family-sans);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(255, 43, 43, 0.3);
  transition: all 0.3s var(--transition-smooth);
  outline: none;
  
  &:hover {
    background: linear-gradient(135deg, var(--color-accent-light), var(--color-accent));
    box-shadow: 0 6px 16px rgba(232, 19, 19, 0.5);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: rgba(232, 19, 19, 0.3);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
`;

const BallotsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const RecommendationsTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  gap: 1rem;
`;

const RecommendationsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-right: 0.5rem;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    -webkit-appearance: none;
  }
  
  &::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 77, 77, 0.5);
    border-radius: 999px;
    min-height: 40px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 77, 77, 0.7);
  }
  
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 77, 77, 0.5) rgba(255, 255, 255, 0.05);
`;

const ChatWrapper = styled.div`
  flex-shrink: 0;
  height: 400px;
  max-height: 400px;
`;

type ReportType = 'insights' | 'key_insights' | 'chat';

const TabContentWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: visible;
`;

const TabContent = styled.div<{ $isVisible: boolean; $isEntering: boolean }>`
  position: ${props => props.$isVisible ? 'relative' : 'absolute'};
  top: 0;
  left: 0;
  right: 0;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.25s ease-in-out;
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  pointer-events: ${props => props.$isVisible ? 'auto' : 'none'};
  z-index: ${props => props.$isVisible ? 1 : 0};
  overflow: visible;
`;

interface ReportsProps {
  onGoToHome?: () => void;
}

export interface ReportsRef {
  handleExportReport: () => void;
}


export const Reports = forwardRef<ReportsRef, ReportsProps & { userTeamMode?: boolean }>(({ onGoToHome, userTeamMode }, ref) => {
  
  const { user } = useAuth();
  const [activeReport, setActiveReport] = useState<ReportType>('insights');
  const [displayReport, setDisplayReport] = useState<ReportType>('insights');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const insightsScrollRef = useRef<HTMLDivElement>(null);
  const [activeDot, setActiveDot] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Handle tab change with fade animation
  const handleTabChange = useCallback((newTab: ReportType) => {
    if (newTab === activeReport || isTransitioning) return;
    
    setIsTransitioning(true);
    // Fade out current tab (250ms)
    setTimeout(() => {
      setDisplayReport(newTab);
      setActiveReport(newTab);
      const tabLabel =
        newTab === 'key_insights' ? 'recommendations' : newTab === 'chat' ? 'chat' : 'insights';
      trackEvent('report_tab_click', {
        tab: newTab,
        tabLabel,
        mode: userTeamMode ? 'user' : 'opponent',
        userEmail: user?.email
      });
      // Small delay before starting fade in
      setTimeout(() => {
        setIsTransitioning(false);
      }, 10);
    }, 250); // Wait for fade out to complete
  }, [activeReport, isTransitioning, user?.id, userTeamMode]);
  
  // Insights tab controls
  const [insightsStatsType, setInsightsStatsType] = useState<'team' | 'players' | 'lineups'>('team');
  const [selectedSplits, setSelectedSplits] = useState<string[]>([]);
  const [availableSplits, setAvailableSplits] = useState<string[]>([]);
  const [isLoadingSplits, setIsLoadingSplits] = useState(false);
  
  // Insights stats data state (kept for potential future use)
  const [insightsTeamStats, setInsightsTeamStats] = useState<any>(null);
  const [insightsPlayerStats, setInsightsPlayerStats] = useState<any[]>([]);
  const [insightsLineupStats, setInsightsLineupStats] = useState<any[]>([]);
  
  // Multi-select dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [splitsDropdownOpenUpward, setSplitsDropdownOpenUpward] = useState(false);
  const splitsDropdownRef = useRef<HTMLDivElement>(null);
  
  const prevSplitCountRef = useRef<number>(selectedSplits.length);
  const prevSplitsRef = useRef<string[]>(selectedSplits);
  const prevStatsTypeRef = useRef<'team' | 'players' | 'lineups'>(insightsStatsType);
  const prevConfigKeyRef = useRef<string | null>(null);

  const areSplitsEqual = useCallback((current: string[], previous: string[]) => {
    if (current.length !== previous.length) {
      return false;
    }

    return current.every((split, index) => split === previous[index]);
  }, []);
  
  // Legacy stats types (kept for compatibility)
  const [teamStatsType, setTeamStatsType] = useState<'traditional' | 'advanced'>('traditional');
  const [playerStatsType, setPlayerStatsType] = useState<'traditional' | 'advanced'>('traditional');
  const [lineupsStatsType, setLineupsStatsType] = useState<'by_minutes' | 'by_efficiency'>('by_minutes');
  const [previousOpponentStats, setPreviousOpponentStats] = useState<any>(null);
  const [opponentPlayers, setOpponentPlayers] = useState<any[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [opponentLineups, setOpponentLineups] = useState<any[]>([]);
  const [isLoadingLineups, setIsLoadingLineups] = useState(false);
  // Note: teamRecommendations and isLoadingRecommendations are now handled by TeamContext
  
  // State for offensive and defensive recommendations
  const [offensiveRecommendationsData, setOffensiveRecommendationsData] = useState<any>(null);
  const [defensiveRecommendationsData, setDefensiveRecommendationsData] = useState<any>(null);
  const [isLoadingOffensiveRecommendations, setIsLoadingOffensiveRecommendations] = useState(false);
  const [isRefreshingOffensive, setIsRefreshingOffensive] = useState(false);
  const [isRefreshingDefensive, setIsRefreshingDefensive] = useState(false);
  const [isLoadingDefensiveRecommendations, setIsLoadingDefensiveRecommendations] = useState(false);
  
  // State for controlling recommendation visibility
  const [showAllOffensiveRecommendations, setShowAllOffensiveRecommendations] = useState(false);
  const [showAllDefensiveRecommendations, setShowAllDefensiveRecommendations] = useState(false);
  
  // State for tracking loading states of individual recommendations
  const [loadingRecommendations, setLoadingRecommendations] = useState<Set<string>>(new Set());
  const [approvingRecommendations, setApprovingRecommendations] = useState<Set<string>>(new Set());
  
  // State for decline feedback modal
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [decliningRecommendationId, setDecliningRecommendationId] = useState<string | null>(null);
  const [decliningRecommendationType, setDecliningRecommendationType] = useState<'offensive' | 'defensive' | null>(null);
  const [decliningRecommendationTitle, setDecliningRecommendationTitle] = useState<string | null>(null);
  
  // Error recovery state - track if user has seen error to prevent infinite retries
  const [lastStatsErrorKey, setLastStatsErrorKey] = useState<string | null>(null);
  const [lastInsightsErrorKey, setLastInsightsErrorKey] = useState<string | null>(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);

  useEffect(() => {
  }, [user?.role]);

  const [chatError, setChatError] = useState<string | null>(null);
  
  // Note: insightsLoadedStages removed - insights are now handled by TeamContext
  
  // State for tracking which insights are refreshing
  const [refreshingInsights, setRefreshingInsights] = useState<Set<string>>(new Set());
  const [copiedInsight, setCopiedInsight] = useState<string | null>(null);
  
  // Copy insight text to clipboard
  const handleCopyInsight = useCallback(async (insight: any, insightKey: string) => {
    try {
      // Copy only the description (caption)
      await navigator.clipboard.writeText(insight.caption);
      setCopiedInsight(insightKey);
      
      // Reset the checkmark after 2 seconds
      setTimeout(() => {
        setCopiedInsight(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy insight:', err);
    }
  }, []);
  
  // Note: hasInitialLoadCompleted removed - no longer needed with simplified stats loading
  
  // Removed player recommendations state - now using strategic recommendations
  
  // Note: playerRecommendations, lineupsRecommendations and their loading states are now handled by TeamContext
  
  // Cache for insights data
  const [insightsCache, setInsightsCache] = useState<Map<string, any>>(new Map());
  const [playerInsightsCache, setPlayerInsightsCache] = useState<Map<string, any>>(new Map());
  const [lineupsInsightsCache, setLineupsInsightsCache] = useState<Map<string, any>>(new Map());
  
  // Cache for recommendations data
  const [offensiveRecommendationsCache, setOffensiveRecommendationsCache] = useState<Map<string, any>>(() =>
    getRecommendationCacheSnapshot('offensive')
  );
  const [defensiveRecommendationsCache, setDefensiveRecommendationsCache] = useState<Map<string, any>>(() =>
    getRecommendationCacheSnapshot('defensive')
  );

  // Cache for stats data
  const [statsCache, setStatsCache] = useState<Map<string, any>>(new Map());

  const getRecommendationsCacheKey = (
    opponentTeamId: string,
    ourTeamId: string,
    seasonId?: string,
    stage?: string,
    type: 'offensive' | 'defensive' = 'offensive'
  ) => {
    return buildRecommendationCacheKey(type, opponentTeamId, ourTeamId, seasonId, stage);
  };

  const clearCache = () => {
    console.log('🔄 Clearing all caches');
    setInsightsCache(new Map());
    setPlayerInsightsCache(new Map());
    setLineupsInsightsCache(new Map());
    setOffensiveRecommendationsCache(new Map());
    setDefensiveRecommendationsCache(new Map());
    setStatsCache(new Map()); // Add stats cache clearing
    setOffensiveRecommendationsData(null);
    setDefensiveRecommendationsData(null);
    clearGlobalRecommendationCaches();
  };

  // Recommendations cache functions
  const getCachedRecommendations = useCallback((
    opponentTeamId: string,
    ourTeamId: string,
    seasonId?: string,
    stage?: string,
    type: 'offensive' | 'defensive' = 'offensive'
  ) => {
    const cacheKey = getRecommendationsCacheKey(opponentTeamId, ourTeamId, seasonId, stage, type);
    const cache = type === 'defensive' ? defensiveRecommendationsCache : offensiveRecommendationsCache;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    return getRecommendationCache(type, opponentTeamId, ourTeamId, seasonId, stage);
  }, [defensiveRecommendationsCache, offensiveRecommendationsCache]);

  const setCachedRecommendations = (opponentTeamId: string, ourTeamId: string, data: any, seasonId?: string, stage?: string, type: 'offensive' | 'defensive' = 'offensive') => {
    const cacheKey = getRecommendationsCacheKey(opponentTeamId, ourTeamId, seasonId, stage, type);
    setRecommendationCache(type, opponentTeamId, ourTeamId, data, seasonId, stage);
    if (type === 'defensive') {
      setDefensiveRecommendationsCache(prev => new Map(prev).set(cacheKey, data));
    } else {
      setOffensiveRecommendationsCache(prev => new Map(prev).set(cacheKey, data));
    }
  };


  // Normalize backend recommendations payloads (supports several shapes and nested keys)
  const extractRecommendations = (data: any): string[] => {
    if (!data) return [];
    const root = data?.report ?? data;

    const toStringArray = (arr: any): string[] => Array.isArray(arr) ? arr.filter((x: any) => typeof x === 'string') : [];

    // Direct known shapes
    if (Array.isArray(root?.recommendations)) return toStringArray(root.recommendations);
    if (Array.isArray(root?.recommendation)) return toStringArray(root.recommendation);

    // Search shallow nested props
    for (const key of Object.keys(root || {})) {
      const val = (root as any)[key];
      if (key.toLowerCase().includes('recommend') && Array.isArray(val)) {
        const out = toStringArray(val);
        if (out.length) return out;
      }
    }

    // Deep search limited breadth/levels for any array<string> under keys containing 'recommend'
    const queue: any[] = [root];
    const visited = new Set<any>();
    let depth = 0;
    while (queue.length && depth < 4) {
      const levelCount = queue.length;
      for (let i = 0; i < levelCount; i++) {
        const node = queue.shift();
        if (!node || typeof node !== 'object' || visited.has(node)) continue;
        visited.add(node);
        for (const [k, v] of Object.entries(node)) {
          if (k.toLowerCase().includes('recommend')) {
            if (Array.isArray(v)) {
              const out = toStringArray(v);
              if (out.length) return out;
            }
          }
          if (v && typeof v === 'object') queue.push(v);
        }
      }
      depth += 1;
    }
    return [];
  };

  const { reportData } = useReport();
  const {
    matchupConfig,
    selectedLeague,
    opponentTeamStats,
    userTeamStats,
    isStatsLoading,
    statsError,
    teamInsights,
    playerInsights,
    lineupsInsights,
    opponentTeamInsights,
    opponentPlayerInsights,
    opponentLineupsInsights,
    isInsightsLoading,
    insightsError,
    loadTeamStats,
    refreshTeamStats,
    // Current stats type for selective loading
    currentStatsType,
    setCurrentStatsType,
    // Selective stats loading methods
    loadTeamStatsOnly,
    loadPlayerStatsOnly,
    loadLineupStatsOnly,
    clearCurrentStats,
    // Selective insights fetching methods
    fetchTeamInsights,
    fetchPlayerInsights,
    fetchLineupInsights,
    // Insights refresh methods
    refreshTeamInsights,
    refreshPlayerInsights,
    refreshLineupInsights,
    
    // Opponent insights refresh methods
    refreshOpponentTeamInsights,
    refreshOpponentPlayerInsights,
    refreshOpponentLineupInsights,
    
    // Individual insight refresh methods (no global loading state)
    refreshIndividualTeamInsights,
    refreshIndividualPlayerInsights,
    refreshIndividualLineupInsights,
    refreshIndividualOpponentTeamInsights,
    refreshIndividualOpponentPlayerInsights,
    refreshIndividualOpponentLineupInsights,
    // Splits data
    teamSplits,
    playerSplits,
    lineupSplits,
    defaultSplits,
    loadTeamSplits,
    loadPlayersSplits,
    loadLineupsSplits,
    // Clear all insights
    clearAllInsights
  } = useTeam();
  const matchupConfigRef = useRef(matchupConfig);

  useLayoutEffect(() => {
    matchupConfigRef.current = matchupConfig;
  }, [matchupConfig]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRecommendationCacheUpdate = (event: Event) => {
      const { detail } = event as CustomEvent<RecommendationCacheEventDetail>;
      if (!detail?.type || !detail?.key) return;

      if (detail.type === 'defensive') {
        setDefensiveRecommendationsCache(prev => new Map(prev).set(detail.key, detail.data));
      } else {
        setOffensiveRecommendationsCache(prev => new Map(prev).set(detail.key, detail.data));
      }

      const currentConfig = matchupConfigRef.current;
      if (!currentConfig?.opponent_team || !currentConfig?.user_team) {
        return;
      }

      const stage = currentConfig.matchup_type || 'regular_season';
      const currentKey = buildRecommendationCacheKey(detail.type, currentConfig.opponent_team.teamId, currentConfig.user_team.teamId, currentConfig.season, stage);

      if (currentKey !== detail.key) {
        return;
      }

      if (detail.type === 'offensive') {
        setOffensiveRecommendationsData(detail.data);
        setIsLoadingOffensiveRecommendations(false);
      } else {
        setDefensiveRecommendationsData(detail.data);
        setIsLoadingDefensiveRecommendations(false);
      }
    };

    window.addEventListener(RECOMMENDATIONS_CACHE_EVENT, handleRecommendationCacheUpdate as EventListener);
    return () => {
      window.removeEventListener(RECOMMENDATIONS_CACHE_EVENT, handleRecommendationCacheUpdate as EventListener);
    };
  }, []);

  // Cache utility functions
  const getCacheKey = (teamId: string, seasonId?: string, type: string = 'team') => {
    if (seasonId) {
      return `${type}_insights_${teamId}_${seasonId}`;
    }
    return `${type}_insights_${teamId}`;
  };

  // Generate unique key for current matchup config including splits
  const getConfigKey = useCallback((includeStatsType: boolean = false) => {
    if (!matchupConfig) return null;
    const splitsKey = selectedSplits.length > 0 ? selectedSplits.sort().join(',') : 'default';
    const statsTypeKey = includeStatsType ? `_${insightsStatsType}` : '';
    return `${matchupConfig.user_team.teamId}_${matchupConfig.opponent_team.teamId}_${matchupConfig.season}_${matchupConfig.matchup_type}_${splitsKey}${statsTypeKey}`;
  }, [matchupConfig, selectedSplits, insightsStatsType]);

  // Note: Initial stats loading is now handled by the main loadStats useEffect above

  // Sync insightsStatsType with TeamContext currentStatsType
  useEffect(() => {
    setCurrentStatsType(insightsStatsType);
  }, [insightsStatsType, setCurrentStatsType]);

  // Clear stats data when switching between stats types
  useEffect(() => {
    // Clear stats in TeamContext to remove old data from previous stats type
    clearCurrentStats();
    
    // Clear all insights in TeamContext when stats type changes (we query DB fresh)
    clearAllInsights();
    
    // Clear ALL cache entries to force fresh load when switching stats types
    // This ensures we get fresh data for the new stats type
    setStatsCache(new Map());
    
    // Clear insights caches as well since we query the database
    setInsightsCache(new Map());
    setPlayerInsightsCache(new Map());
    setLineupsInsightsCache(new Map());
    
    // Clear error keys to allow retry when switching stats types
    setLastStatsErrorKey(null);
    setLastInsightsErrorKey(null);
  }, [insightsStatsType, clearCurrentStats, clearAllInsights]);

  // Request splits from TeamContext when stats type changes
  useEffect(() => {
    const requestSplitsFromTeamContext = async () => {
      if (!selectedLeague) return;
      
      try {
        switch (insightsStatsType) {
          case 'team':
            await loadTeamSplits(selectedLeague.abbreviation);
            break;
          case 'players':
            await loadPlayersSplits(selectedLeague.abbreviation);
            break;
          case 'lineups':
            await loadLineupsSplits(selectedLeague.abbreviation);
            break;
        }
      } catch (error) {
        console.error(`❌ Error requesting ${insightsStatsType} splits from TeamContext:`, error);
      }
    };
    
    requestSplitsFromTeamContext();
  }, [insightsStatsType, selectedLeague, loadTeamSplits, loadPlayersSplits, loadLineupsSplits]);

  // Update available splits when splits data changes
  useEffect(() => {
    let splits: string[] = [];
    switch (insightsStatsType) {
      case 'team':
        splits = teamSplits || [];
        break;
      case 'players':
        splits = playerSplits || [];
        break;
      case 'lineups':
        splits = lineupSplits || [];
        break;
    }
    setAvailableSplits(splits);
  }, [teamSplits, playerSplits, lineupSplits, insightsStatsType]);

  // Auto-select default splits (wins and losses) when they become available
  useEffect(() => {
    // Only auto-select if no splits are currently selected
    if (selectedSplits.length > 0) {
      return;
    }

    // Find wins and losses splits (exact match only, case-insensitive)
    const winsVariants = ['wins', 'win', 'W'];
    const lossesVariants = ['losses', 'loss', 'L'];

    const selectedDefaults: string[] = [];

    // Look for wins split (exact match only)
    const winsSplit = availableSplits.find(split => 
      winsVariants.some(variant => split.toLowerCase() === variant.toLowerCase())
    );
    if (winsSplit) {
      selectedDefaults.push(winsSplit);
    }

    // Look for losses split (exact match only)
    const lossesSplit = availableSplits.find(split => 
      lossesVariants.some(variant => split.toLowerCase() === variant.toLowerCase())
    );
    if (lossesSplit) {
      selectedDefaults.push(lossesSplit);
    }

    // Set default splits if we found any
    if (selectedDefaults.length > 0) {
      setSelectedSplits(selectedDefaults);
    }
  }, [availableSplits, selectedSplits.length]);

  // Note: Removed automatic initialization of selectedSplits from defaultSplits
  // User will manually select splits to trigger API calls

  // Calculate if splits dropdown should open upward BEFORE opening
  const calculateSplitsDropdownDirection = (): boolean => {
    if (!splitsDropdownRef.current) return false;
    
    const buttonRect = splitsDropdownRef.current.getBoundingClientRect();
    const listHeight = 240; // max-height of dropdown list
    const gap = 4; // gap between button and list
    
    // Find the modal container or closest container with boundaries
    let container: HTMLElement | null = splitsDropdownRef.current.parentElement;
    let containerRect: DOMRect | null = null;
    
    // Look for modal container by checking parent elements
    while (container && container !== document.body) {
      // Check if this is a modal container
      if (container.hasAttribute('data-modal-container')) {
        containerRect = container.getBoundingClientRect();
        break;
      }
      
      // Also check if parent has a max-height (likely a modal/container)
      const style = window.getComputedStyle(container);
      const rect = container.getBoundingClientRect();
      
      // If container has max-height and is smaller than viewport, use it
      if (style.maxHeight !== 'none' && style.maxHeight !== 'auto' && 
          rect.height < window.innerHeight - 100) {
        containerRect = rect;
        break;
      }
      
      container = container.parentElement;
    }
    
    // Calculate available space
    let spaceBelow: number;
    let spaceAbove: number;
    
    if (containerRect) {
      // Use container boundaries
      spaceBelow = containerRect.bottom - buttonRect.bottom - gap;
      spaceAbove = buttonRect.top - containerRect.top - gap;
    } else {
      // Fallback to viewport
      spaceBelow = window.innerHeight - buttonRect.bottom - gap;
      spaceAbove = buttonRect.top - gap;
    }
    
    // Open upward if not enough space below but enough space above
    // OR if opening down would overflow the container
    const wouldOverflowBelow = spaceBelow < listHeight;
    const hasEnoughSpaceAbove = spaceAbove >= listHeight;
    
    return wouldOverflowBelow && (hasEnoughSpaceAbove || spaceAbove > spaceBelow);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Monitor statsError and set error key when error occurs
  useEffect(() => {
    if (statsError) {
      const cacheKey = getConfigKey(true);
      if (cacheKey) {
        const currentErrorKey = `${cacheKey}_error`;
        console.log('❌ Stats error detected, setting error key to prevent retry loop:', currentErrorKey);
        setLastStatsErrorKey(currentErrorKey);
      }
    }
  }, [statsError, getConfigKey]);

  // Simplified effect for loading stats only - insights are handled by TeamContext
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const loadStats = async () => {
        if (!matchupConfig) return;
        
        // Only trigger API call when user has selected splits
        if (selectedSplits.length === 0) {
          return;
        }
        
        const cacheKey = getConfigKey(true); // Include stats type in cache key
        if (!cacheKey) return;
        
        // Generate error key to track failed requests for this config
        const currentErrorKey = `${cacheKey}_error`;
        
        // If we already have an error for this exact config, don't retry
        if (lastStatsErrorKey === currentErrorKey) {
          return;
        }
        
        // Check cache first
        const cachedStats = statsCache.get(cacheKey);
        if (cachedStats) {
          // Stats are already loaded in TeamContext, no need to apply cached data
          return;
        }
        
        
      try {
      
      let statsLoaded = false;
      
      switch (insightsStatsType as 'team' | 'players' | 'lineups') {
        case 'team':
            await loadTeamStatsOnly(selectedSplits);
            statsLoaded = true;
            break;
          case 'players':
            await loadPlayerStatsOnly(selectedSplits);
            statsLoaded = true;
            break;
          case 'lineups':
            await loadLineupStatsOnly(selectedSplits);
            statsLoaded = true;
            break;
        }
        
        // Cache the results after successful fetch
        if (statsLoaded) {
          setStatsCache(prev => new Map(prev).set(cacheKey, { 
            statsType: insightsStatsType as 'team' | 'players' | 'lineups',
            splits: selectedSplits,
            timestamp: Date.now()
          }));
          
          // Clear error key on success
          setLastStatsErrorKey(null);
        }
      } catch (error) {
        console.error(`❌ Error loading ${insightsStatsType} stats:`, error);
        // Set error key to prevent retry loop
        setLastStatsErrorKey(currentErrorKey);
      }
    };
    
    loadStats();
    }, 300); // 300ms debounce delay
    
    return () => clearTimeout(timeoutId);
  }, [insightsStatsType, selectedSplits, matchupConfig, loadTeamStatsOnly, loadPlayerStatsOnly, loadLineupStatsOnly, statsCache, getConfigKey, lastStatsErrorKey]);

  // Monitor insightsError and set error key when error occurs
  useEffect(() => {
    if (insightsError) {
      const configKey = getConfigKey();
      if (configKey) {
        const currentInsightsErrorKey = `${configKey}_insights_error`;
        console.log('❌ Insights error detected, setting error key to prevent retry loop:', currentInsightsErrorKey);
        setLastInsightsErrorKey(currentInsightsErrorKey);
      }
    }
  }, [insightsError, getConfigKey]);

  // Auto-fetch insights when stats are loaded and insights tab is active
  useEffect(() => {
    if (!matchupConfig || activeReport !== 'insights') {
      prevSplitCountRef.current = selectedSplits.length;
      prevSplitsRef.current = selectedSplits;
      prevStatsTypeRef.current = insightsStatsType;
      return;
    }

    // Only wait for insights loading, not stats loading - they should be independent
    if (isInsightsLoading) {
      return;
    }

    // Check if configuration itself changed (not just splits)
    const currentConfigKey = getConfigKey();
    const configChanged = currentConfigKey && currentConfigKey !== prevConfigKeyRef.current;
    
    // If config changed, clear error state to allow retry
    if (configChanged) {
      setLastInsightsErrorKey(null);
      prevConfigKeyRef.current = currentConfigKey;
      // Reset previous state to force fetch by using values that will definitely trigger change
      prevSplitCountRef.current = -1;
      prevSplitsRef.current = [];
      // Use a different stats type to force change detection
      prevStatsTypeRef.current = insightsStatsType === 'team' ? 'players' : 'team';
    }

    const previousSplitCount = prevSplitCountRef.current;
    const previousSplits = prevSplitsRef.current;
    const previousStatsType = prevStatsTypeRef.current;

    const splitCount = selectedSplits.length;
    const hasEvenSplitCount = splitCount >= 2 && splitCount % 2 === 0;
    
    // Don't fetch insights until we have at least 2 splits (e.g., wins/losses for comparison)
    // The backend requires comparison splits to generate meaningful insights
    if (!hasEvenSplitCount) {
      prevSplitCountRef.current = splitCount;
      prevSplitsRef.current = selectedSplits;
      prevStatsTypeRef.current = insightsStatsType;
      return;
    }
    
    const splitsChanged =
      splitCount !== previousSplitCount || !areSplitsEqual(selectedSplits, previousSplits);
    const isStatsTypeChange = insightsStatsType !== previousStatsType;
    const wasEvenSplitCount = previousSplitCount >= 2 && previousSplitCount % 2 === 0;
    const isTransitionToEven = hasEvenSplitCount && (!wasEvenSplitCount || !areSplitsEqual(selectedSplits, previousSplits));
    // Force fetch if config changed, stats type changes, or transitioning to even splits
    const meetsChangeCriteria = configChanged || isStatsTypeChange || (hasEvenSplitCount && splitsChanged);

    if (!meetsChangeCriteria) {
      prevSplitCountRef.current = splitCount;
      prevSplitsRef.current = selectedSplits;
      prevStatsTypeRef.current = insightsStatsType;
      return;
    }

    // Generate error key to track failed insights requests for this config
    const configKey = getConfigKey();
    if (!configKey) {
      prevSplitCountRef.current = splitCount;
      prevSplitsRef.current = selectedSplits;
      prevStatsTypeRef.current = insightsStatsType;
      return;
    }
    const currentInsightsErrorKey = `${configKey}_insights_error`;

    // If we already have an error for this exact config, don't retry
    if (lastInsightsErrorKey === currentInsightsErrorKey) {
      prevSplitCountRef.current = splitCount;
      prevSplitsRef.current = selectedSplits;
      prevStatsTypeRef.current = insightsStatsType;
      return;
    }

    // Check if insights already exist for current config
    const hasInsights =
      (insightsStatsType === 'team' && teamInsights && opponentTeamInsights) ||
      (insightsStatsType === 'players' && playerInsights && opponentPlayerInsights) ||
      (insightsStatsType === 'lineups' && lineupsInsights && opponentLineupsInsights);

    // Force API call when config changes, stats type changes, or transitioning to even splits - skip cache check entirely
    if (configChanged || isStatsTypeChange) {
      // Skip cache check and proceed directly to API call
    } else if (hasInsights && !isTransitionToEven) {
      // Only skip if insights exist AND we're not transitioning to a new even split count
      // AND we're not changing stats types or config
      // Clear error key on success (insights exist)
      setLastInsightsErrorKey(null);
      prevSplitCountRef.current = splitCount;
      prevSplitsRef.current = selectedSplits;
      prevStatsTypeRef.current = insightsStatsType;
      return;
    }
    const splits = selectedSplits.length > 0 ? selectedSplits : undefined;
    switch (insightsStatsType) {
      case 'team':
        fetchTeamInsights(matchupConfig, splits);
        break;
      case 'players':
        fetchPlayerInsights(matchupConfig, splits);
        break;
      case 'lineups':
        fetchLineupInsights(matchupConfig, splits);
        break;
    }

    prevSplitCountRef.current = splitCount;
    prevSplitsRef.current = selectedSplits;
    prevStatsTypeRef.current = insightsStatsType;
    
    // Update config key ref if it changed
    if (currentConfigKey) {
      prevConfigKeyRef.current = currentConfigKey;
    }
  }, [
    matchupConfig,
    activeReport,
    selectedSplits,
    insightsStatsType,
    isStatsLoading,
    isInsightsLoading,
    teamInsights,
    opponentTeamInsights,
    playerInsights,
    opponentPlayerInsights,
    lineupsInsights,
    opponentLineupsInsights,
    fetchTeamInsights,
    fetchPlayerInsights,
    fetchLineupInsights,
    getConfigKey,
    lastInsightsErrorKey,
    areSplitsEqual
  ]);

  // Clear insights when splits or stats type changes
  useEffect(() => {
    // Clear insights based on current stats type
    switch (insightsStatsType) {
      case 'team':
        // TeamContext will handle refetch
        break;
      case 'players':
        // TeamContext will handle refetch
        break;
      case 'lineups':
        // TeamContext will handle refetch
        break;
    }
    
    // Clear error keys to allow retry when user changes splits/stats type
    setLastStatsErrorKey(null);
    setLastInsightsErrorKey(null);
  }, [selectedSplits, insightsStatsType]);

  // Switch to first tab when in user team mode and currently on key_insights tab
  useEffect(() => {
    if (userTeamMode && activeReport === 'key_insights') {
      setActiveReport('insights');
    }
  }, [userTeamMode, activeReport]);

  // Listen for cache clearing events from TeamContext
  useEffect(() => {
    const handleClearCache = () => {
      clearCache();
    };

    window.addEventListener('clearReportsCache', handleClearCache);
    
    return () => {
      window.removeEventListener('clearReportsCache', handleClearCache);
    };
  }, []);

  // Store previous stats when new stats arrive
  useEffect(() => {
    if (opponentTeamStats && !isStatsLoading) {
      setPreviousOpponentStats(opponentTeamStats);
    }
  }, [opponentTeamStats, isStatsLoading]);

  // Note: Team stats loading is now handled by the first useEffect above
  // which properly handles splits and dependencies


  // Note: Player data is now handled by TeamContext - no direct API calls needed

  // Note: Lineup data is now handled by TeamContext - no direct API calls needed

  // Note: fetchInsightsForTeam is now handled by TeamContext.fetchAllInsights

  // Note: fetchPlayerInsights is now handled by TeamContext.fetchAllInsights

  // Note: fetchLineupsInsights is now handled by TeamContext.fetchAllInsights

  // Fetch offensive recommendations
  const fetchOffensiveRecommendations = useCallback(async (opponentTeamId: string, ourTeamId: string) => {
    // Validate team configuration
    if (!matchupConfig) {
      console.log('No matchup config available for offensive recommendations');
      setOffensiveRecommendationsData(null);
      return;
    }

    if (!ourTeamId || !opponentTeamId || ourTeamId === 'undefined' || opponentTeamId === 'undefined') {
      console.log('Invalid team IDs for offensive recommendations:', { ourTeamId, opponentTeamId });
      setOffensiveRecommendationsData(null);
      return;
    }

    if (!matchupConfig.season || !matchupConfig.matchup_type) {
      console.log('Missing season or matchup type for offensive recommendations');
      setOffensiveRecommendationsData(null);
      return;
    }

    if (!selectedLeague?.abbreviation) {
      console.log('No league selected for offensive recommendations');
      setOffensiveRecommendationsData(null);
      return;
    }

    const cacheKey = buildRecommendationCacheKey('offensive', opponentTeamId, ourTeamId, matchupConfig?.season, matchupConfig?.matchup_type || "regular_season");
    if (isRecommendationRequestInFlight('offensive', cacheKey)) {
      console.log('⏳ Offensive recommendations request already in flight, skipping duplicate fetch for', cacheKey);
      return;
    }

    // Check cache first
    const cachedData = getCachedRecommendations(opponentTeamId, ourTeamId, matchupConfig?.season, matchupConfig?.matchup_type || "regular_season", 'offensive');
    if (cachedData) {
      console.log(`📦 Using cached offensive recommendations for opponent:`, opponentTeamId, 'our team:', ourTeamId, 'season:', matchupConfig?.season);
      setOffensiveRecommendationsData(cachedData);
      return;
    }
    
    console.log('🚀 Starting fetchOffensiveRecommendations with:', { opponentTeamId, ourTeamId, season: matchupConfig?.season });
    setIsLoadingOffensiveRecommendations(true);
    markRecommendationRequestInFlight('offensive', cacheKey);
    
    try {
      // Prepare match config
      const matchConfig = {
        stage: matchupConfig?.matchup_type || "regular",
        userTeam: {
          teamId: ourTeamId,
          teamName: matchupConfig?.user_team?.teamName || ''
        },
        opponentTeam: {
          teamId: opponentTeamId,
          teamName: matchupConfig?.opponent_team?.teamName || ''
        },
        seasonId: matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`,
        league: selectedLeague?.abbreviation
      };
      const recommendations = await apiService.getOffensiveRecommendations(matchConfig);
      console.log('✅ Offensive recommendations received:', recommendations);
      setCachedRecommendations(opponentTeamId, ourTeamId, recommendations, matchupConfig?.season, matchupConfig?.matchup_type || "regular_season", 'offensive');
      setOffensiveRecommendationsData(recommendations);
    } catch (error) {
      console.error(`❌ Failed to fetch offensive recommendations:`, error);
      setOffensiveRecommendationsData(null);
    } finally {
      setIsLoadingOffensiveRecommendations(false);
      clearRecommendationRequestInFlight('offensive', cacheKey);
    }
  }, [
    matchupConfig,
    getCachedRecommendations,
    setOffensiveRecommendationsData,
    setIsLoadingOffensiveRecommendations,
    setCachedRecommendations,
    selectedLeague
  ]);

  // Fetch defensive recommendations
  const fetchDefensiveRecommendations = useCallback(async (opponentTeamId: string, ourTeamId: string) => {
    // Validate team configuration
    if (!matchupConfig) {
      console.log('No matchup config available for defensive recommendations');
      setDefensiveRecommendationsData(null);
      return;
    }

    if (!ourTeamId || !opponentTeamId || ourTeamId === 'undefined' || opponentTeamId === 'undefined') {
      console.log('Invalid team IDs for defensive recommendations:', { ourTeamId, opponentTeamId });
      setDefensiveRecommendationsData(null);
      return;
    }

    if (!matchupConfig.season || !matchupConfig.matchup_type) {
      console.log('Missing season or matchup type for defensive recommendations');
      setDefensiveRecommendationsData(null);
      return;
    }

    if (!selectedLeague?.abbreviation) {
      console.log('No league selected for defensive recommendations');
      setDefensiveRecommendationsData(null);
      return;
    }

    const cacheKey = buildRecommendationCacheKey('defensive', opponentTeamId, ourTeamId, matchupConfig?.season, matchupConfig?.matchup_type || "regular_season");
    if (isRecommendationRequestInFlight('defensive', cacheKey)) {
      console.log('⏳ Defensive recommendations request already in flight, skipping duplicate fetch for', cacheKey);
      return;
    }

    // Check cache first
    const cachedData = getCachedRecommendations(opponentTeamId, ourTeamId, matchupConfig?.season, matchupConfig?.matchup_type || "regular_season", 'defensive');
    if (cachedData) {
      console.log(`📦 Using cached defensive recommendations for opponent:`, opponentTeamId, 'our team:', ourTeamId, 'season:', matchupConfig?.season);
      setDefensiveRecommendationsData(cachedData);
      return;
    }
    
    console.log('🚀 Starting fetchDefensiveRecommendations with:', { opponentTeamId, ourTeamId, season: matchupConfig?.season });
    setIsLoadingDefensiveRecommendations(true);
    markRecommendationRequestInFlight('defensive', cacheKey);
    
    try {
      // Prepare match config
      const matchConfig = {
        stage: matchupConfig?.matchup_type || "regular",
        userTeam: {
          teamId: ourTeamId,
          teamName: matchupConfig?.user_team?.teamName || ''
        },
        opponentTeam: {
          teamId: opponentTeamId,
          teamName: matchupConfig?.opponent_team?.teamName || ''
        },
        seasonId: matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`,
        league: selectedLeague?.abbreviation
      };
      const recommendations = await apiService.getDefensiveRecommendations(matchConfig);
      console.log('✅ Defensive recommendations received:', recommendations);
      setCachedRecommendations(opponentTeamId, ourTeamId, recommendations, matchupConfig?.season, matchupConfig?.matchup_type || "regular_season", 'defensive');
      setDefensiveRecommendationsData(recommendations);
    } catch (error) {
      console.error(`❌ Failed to fetch defensive recommendations:`, error);
      setDefensiveRecommendationsData(null);
    } finally {
      setIsLoadingDefensiveRecommendations(false);
      clearRecommendationRequestInFlight('defensive', cacheKey);
    }
  }, [
    matchupConfig,
    getCachedRecommendations,
    setDefensiveRecommendationsData,
    setIsLoadingDefensiveRecommendations,
    setCachedRecommendations,
    selectedLeague
  ]);

  // Handler for approving offensive recommendations
  const handleApproveOffensive = useCallback(async (recommendationId: string) => {
    if (!selectedLeague?.abbreviation) {
      console.log('No league selected for approving offensive recommendation');
      return;
    }
    
    // Add to approving set
    setApprovingRecommendations(prev => new Set(prev).add(recommendationId));
    
    try {
      console.log('✅ Approving offensive recommendation:', recommendationId);
      await apiService.approveOffensiveRecommendation([recommendationId], selectedLeague.abbreviation);
      console.log('✅ Offensive recommendation approved successfully');
      
      // Show success message briefly
      setTimeout(() => {
        setApprovingRecommendations(prev => {
          const newSet = new Set(prev);
          newSet.delete(recommendationId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('❌ Failed to approve offensive recommendation:', error);
      setApprovingRecommendations(prev => {
        const newSet = new Set(prev);
        newSet.delete(recommendationId);
        return newSet;
      });
    }
  }, []);

  // Handler for declining offensive recommendations - shows feedback modal
  const handleDeclineOffensive = useCallback((recommendationId: string) => {
    // Find the recommendation to get its title
    const recommendation = offensiveRecommendationsData?.recommendations?.find((r: any) => r.id === recommendationId);
    const recommendationTitle = recommendation?.focus_area || null;
    
    // Set up modal state
    setDecliningRecommendationId(recommendationId);
    setDecliningRecommendationType('offensive');
    setDecliningRecommendationTitle(recommendationTitle);
    setDeclineModalOpen(true);
  }, [offensiveRecommendationsData]);

  // Handler for submitting decline feedback for offensive recommendations
  const handleSubmitDeclineFeedbackOffensive = useCallback(async (feedback: string) => {
    if (!decliningRecommendationId || !selectedLeague?.abbreviation) {
      throw new Error('Missing recommendation ID or league');
    }
    
    // Prepare match config
    const matchConfig = {
      stage: matchupConfig?.matchup_type || "regular",
      userTeam: {
        teamId: matchupConfig?.user_team?.teamId || '',
        teamName: matchupConfig?.user_team?.teamName || ''
      },
      opponentTeam: {
        teamId: matchupConfig?.opponent_team?.teamId || '',
        teamName: matchupConfig?.opponent_team?.teamName || ''
      },
      seasonId: matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`,
      league: selectedLeague?.abbreviation
    };
    
    // Submit feedback to backend
    await apiService.submitDeclineFeedback(
      decliningRecommendationId,
      feedback,
      'offensive',
      matchConfig
    );
    
    // Remove the declined recommendation from the list (without refreshing)
    setOffensiveRecommendationsData((prev: any) => {
      if (!prev) return null;
      
      const updatedRecommendations = prev.recommendations.filter((rec: any) => rec.id !== decliningRecommendationId);
      
      if (updatedRecommendations.length === 0) {
        return null;
      }
      
      const updatedData = {
        ...prev,
        recommendations: updatedRecommendations
      };
      
      // Update cache
      setCachedRecommendations(
        matchupConfig?.opponent_team?.teamId || '',
        matchupConfig?.user_team?.teamId || '',
        updatedData,
        matchupConfig?.season,
        matchupConfig?.matchup_type || "regular_season",
        'offensive'
      );
      
      return updatedData;
    });
    
    console.log('✅ Decline feedback submitted and recommendation removed');
  }, [decliningRecommendationId, matchupConfig, selectedLeague]);

  // Handler for refreshing all offensive recommendations
  const handleRefreshAllOffensive = useCallback(async () => {
    if (!offensiveRecommendationsData?.recommendations?.length) return;
    
    if (!selectedLeague?.abbreviation) {
      console.log('No league selected for refreshing all offensive recommendations');
      return;
    }
    
    // Get all offensive recommendation IDs
    const allRids = offensiveRecommendationsData.recommendations.map((r: any) => r.id);
    
    // Add all to loading set
    setLoadingRecommendations(prev => {
      const newSet = new Set(prev);
      allRids.forEach((id: string) => newSet.add(id));
      return newSet;
    });
    
    setIsRefreshingOffensive(true);
    
    try {
      const userTeamInsightsIds = teamInsights?.insights?.map((i: any) => i.id) || [];
      const opponentTeamInsightsIds = opponentTeamInsights?.insights?.map((i: any) => i.id) || [];
      
      const matchConfig = {
        stage: matchupConfig?.matchup_type || "regular",
        userTeam: {
          teamId: matchupConfig?.user_team?.teamId || '',
          teamName: matchupConfig?.user_team?.teamName || ''
        },
        opponentTeam: {
          teamId: matchupConfig?.opponent_team?.teamId || '',
          teamName: matchupConfig?.opponent_team?.teamName || ''
        },
        seasonId: matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`,
        league: selectedLeague?.abbreviation
      };
      
      const response = await apiService.refreshOffensiveRecommendation(
        matchConfig,
        selectedSplits,
        userTeamInsightsIds,
        opponentTeamInsightsIds,
        allRids
      );
      
      if (response.recommendations && response.recommendations.length > 0) {
        const newData = {
          ...response,
          recommendations: response.recommendations
        };
        
        // Update state
        setOffensiveRecommendationsData(newData);
        
        // Update cache
        setCachedRecommendations(
          matchupConfig?.opponent_team?.teamId || '',
          matchupConfig?.user_team?.teamId || '',
          newData,
          matchupConfig?.season,
          matchupConfig?.matchup_type || "regular_season",
          'offensive'
        );
      }
    } catch (error) {
      console.error('Failed to refresh all offensive recommendations:', error);
    } finally {
      // Clear loading set
      setLoadingRecommendations(prev => {
        const newSet = new Set(prev);
        allRids.forEach((id: string) => newSet.delete(id));
        return newSet;
      });
      setIsRefreshingOffensive(false);
    }
  }, [offensiveRecommendationsData, teamInsights, opponentTeamInsights, matchupConfig, selectedSplits, setCachedRecommendations]);

  // Handler for refreshing all defensive recommendations
  const handleRefreshAllDefensive = useCallback(async () => {
    if (!defensiveRecommendationsData?.recommendations?.length) return;
    
    if (!selectedLeague?.abbreviation) {
      console.log('No league selected for refreshing all defensive recommendations');
      return;
    }
    
    // Get all defensive recommendation IDs
    const allRids = defensiveRecommendationsData.recommendations.map((r: any) => r.id);
    
    // Add all to loading set
    setLoadingRecommendations(prev => {
      const newSet = new Set(prev);
      allRids.forEach((id: string) => newSet.add(id));
      return newSet;
    });
    
    setIsRefreshingDefensive(true);
    
    try {
      const userTeamInsightsIds = teamInsights?.insights?.map((i: any) => i.id) || [];
      const opponentTeamInsightsIds = opponentTeamInsights?.insights?.map((i: any) => i.id) || [];
      
      const matchConfig = {
        stage: matchupConfig?.matchup_type || "regular",
        userTeam: {
          teamId: matchupConfig?.user_team?.teamId || '',
          teamName: matchupConfig?.user_team?.teamName || ''
        },
        opponentTeam: {
          teamId: matchupConfig?.opponent_team?.teamId || '',
          teamName: matchupConfig?.opponent_team?.teamName || ''
        },
        seasonId: matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`,
        league: selectedLeague?.abbreviation
      };
      
      const response = await apiService.refreshDefensiveRecommendation(
        matchConfig,
        selectedSplits,
        userTeamInsightsIds,
        opponentTeamInsightsIds,
        allRids
      );
      
      if (response.recommendations && response.recommendations.length > 0) {
        const newData = {
          ...response,
          recommendations: response.recommendations
        };
        
        // Update state
        setDefensiveRecommendationsData(newData);
        
        // Update cache
        setCachedRecommendations(
          matchupConfig?.opponent_team?.teamId || '',
          matchupConfig?.user_team?.teamId || '',
          newData,
          matchupConfig?.season,
          matchupConfig?.matchup_type || "regular_season",
          'defensive'
        );
      }
    } catch (error) {
      console.error('Failed to refresh all defensive recommendations:', error);
    } finally {
      // Clear loading set
      setLoadingRecommendations(prev => {
        const newSet = new Set(prev);
        allRids.forEach((id: string) => newSet.delete(id));
        return newSet;
      });
      setIsRefreshingDefensive(false);
    }
  }, [defensiveRecommendationsData, teamInsights, opponentTeamInsights, matchupConfig, selectedSplits, setCachedRecommendations]);

  // Handler for approving defensive recommendations
  const handleApproveDefensive = useCallback(async (recommendationId: string) => {
    if (!selectedLeague?.abbreviation) {
      console.log('No league selected for approving defensive recommendation');
      return;
    }
    
    // Add to approving set
    setApprovingRecommendations(prev => new Set(prev).add(recommendationId));
    
    try {
      console.log('✅ Approving defensive recommendation:', recommendationId);
      await apiService.approveDefensiveRecommendation([recommendationId], selectedLeague.abbreviation);
      console.log('✅ Defensive recommendation approved successfully');
      
      // Show success message briefly
      setTimeout(() => {
        setApprovingRecommendations(prev => {
          const newSet = new Set(prev);
          newSet.delete(recommendationId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('❌ Failed to approve defensive recommendation:', error);
      setApprovingRecommendations(prev => {
        const newSet = new Set(prev);
        newSet.delete(recommendationId);
        return newSet;
      });
    }
  }, []);

  // Handler for declining defensive recommendations - shows feedback modal
  const handleDeclineDefensive = useCallback((recommendationId: string) => {
    // Find the recommendation to get its title
    const recommendation = defensiveRecommendationsData?.recommendations?.find((r: any) => r.id === recommendationId);
    const recommendationTitle = recommendation?.focus_area || null;
    
    // Set up modal state
    setDecliningRecommendationId(recommendationId);
    setDecliningRecommendationType('defensive');
    setDecliningRecommendationTitle(recommendationTitle);
    setDeclineModalOpen(true);
  }, [defensiveRecommendationsData]);

  // Handler for submitting decline feedback for defensive recommendations
  const handleSubmitDeclineFeedbackDefensive = useCallback(async (feedback: string) => {
    if (!decliningRecommendationId || !selectedLeague?.abbreviation) {
      throw new Error('Missing recommendation ID or league');
    }
    
    // Prepare match config
    const matchConfig = {
      stage: matchupConfig?.matchup_type || "regular",
      userTeam: {
        teamId: matchupConfig?.user_team?.teamId || '',
        teamName: matchupConfig?.user_team?.teamName || ''
      },
      opponentTeam: {
        teamId: matchupConfig?.opponent_team?.teamId || '',
        teamName: matchupConfig?.opponent_team?.teamName || ''
      },
      seasonId: matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`,
      league: selectedLeague?.abbreviation
    };
    
    // Submit feedback to backend
    await apiService.submitDeclineFeedback(
      decliningRecommendationId,
      feedback,
      'defensive',
      matchConfig
    );
    
    // Remove the declined recommendation from the list (without refreshing)
    setDefensiveRecommendationsData((prev: any) => {
      if (!prev) return null;
      
      const updatedRecommendations = prev.recommendations.filter((rec: any) => rec.id !== decliningRecommendationId);
      
      if (updatedRecommendations.length === 0) {
        return null;
      }
      
      const updatedData = {
        ...prev,
        recommendations: updatedRecommendations
      };
      
      // Update cache
      setCachedRecommendations(
        matchupConfig?.opponent_team?.teamId || '',
        matchupConfig?.user_team?.teamId || '',
        updatedData,
        matchupConfig?.season,
        matchupConfig?.matchup_type || "regular_season",
        'defensive'
      );
      
      return updatedData;
    });
    
    console.log('✅ Decline feedback submitted and recommendation removed');
  }, [decliningRecommendationId, matchupConfig, selectedLeague]);

  // Unified handler for submitting decline feedback
  const handleSubmitDeclineFeedback = useCallback(async (feedback: string) => {
    if (decliningRecommendationType === 'offensive') {
      await handleSubmitDeclineFeedbackOffensive(feedback);
    } else if (decliningRecommendationType === 'defensive') {
      await handleSubmitDeclineFeedbackDefensive(feedback);
    }
  }, [decliningRecommendationType, handleSubmitDeclineFeedbackOffensive, handleSubmitDeclineFeedbackDefensive]);

  // Handler for closing the decline modal
  const handleCloseDeclineModal = useCallback(() => {
    setDeclineModalOpen(false);
    setDecliningRecommendationId(null);
    setDecliningRecommendationType(null);
    setDecliningRecommendationTitle(null);
  }, []);

  // Removed the conflicting useEffect that was always fetching user team insights

  // Note: Insights are now fetched automatically when configuration changes or on startup
  // No need for tab-triggered insights fetching


  // Function to refresh a specific insight
  const refreshInsight = async (insight: any, insightIndex: number, statsType: string) => {
    const insightKey = `${statsType}_${insightIndex}_${insight.caption}`;
    
    // Add to refreshing set
    setRefreshingInsights(prev => new Set(prev).add(insightKey));
    
    try {
      console.log(`Refreshing insight: ${insight.caption} (${statsType})`);
      
      // Call the appropriate refresh method based on stats type
      let refreshedInsights = null;
      const splits = selectedSplits.length > 0 ? selectedSplits : undefined;
      
      // Determine which team's insights to refresh based on userTeamMode
      const isUserTeam = userTeamMode;
      console.log('🔄 Refreshing insight for:', isUserTeam ? 'USER team' : 'OPPONENT team');
      
      switch (statsType) {
        case 'team':
          if (isUserTeam) {
            refreshedInsights = await refreshIndividualTeamInsights([insight.id], splits, insightIndex);
          } else {
            refreshedInsights = await refreshIndividualOpponentTeamInsights([insight.id], splits, insightIndex);
          }
          break;
        case 'players':
          if (isUserTeam) {
            refreshedInsights = await refreshIndividualPlayerInsights([insight.id], splits, insightIndex);
          } else {
            refreshedInsights = await refreshIndividualOpponentPlayerInsights([insight.id], splits, insightIndex);
          }
          break;
        case 'lineups':
          if (isUserTeam) {
            refreshedInsights = await refreshIndividualLineupInsights([insight.id], splits, insightIndex);
          } else {
            refreshedInsights = await refreshIndividualOpponentLineupInsights([insight.id], splits, insightIndex);
          }
          break;
        default:
          throw new Error(`Unknown stats type: ${statsType}`);
      }
      
      if (refreshedInsights && refreshedInsights.insights && refreshedInsights.insights.length > 0) {
        console.log(`✅ Successfully refreshed insight: ${insight.caption}`, {
          originalInsight: insight,
          refreshedInsights: refreshedInsights.insights,
          statsType
        });
      } else {
        console.warn(`⚠️ No refreshed insights returned for: ${insight.caption}`, {
          refreshedInsights,
          statsType
        });
      }
      
    } catch (error) {
      console.error('Failed to refresh insight:', error);
    } finally {
      // Remove from refreshing set
      setRefreshingInsights(prev => {
        const newSet = new Set(prev);
        newSet.delete(insightKey);
        return newSet;
      });
    }
  };

  // Recommendations watcher: on tab entry or config change, hydrate from cache or show spinner until background prefetch finishes
  useEffect(() => {
    if (activeReport !== 'key_insights') return;

    if (!matchupConfig?.opponent_team || !matchupConfig?.user_team || !selectedLeague?.abbreviation || userTeamMode) {
      setOffensiveRecommendationsData(null);
      setDefensiveRecommendationsData(null);
      setIsLoadingOffensiveRecommendations(false);
      setIsLoadingDefensiveRecommendations(false);
      return;
    }

    const stage = matchupConfig.matchup_type || 'regular_season';

    const cachedOffensive = getCachedRecommendations(
      matchupConfig.opponent_team.teamId,
      matchupConfig.user_team.teamId,
      matchupConfig.season,
      stage,
      'offensive'
    );
    const cachedDefensive = getCachedRecommendations(
      matchupConfig.opponent_team.teamId,
      matchupConfig.user_team.teamId,
      matchupConfig.season,
      stage,
      'defensive'
    );

    if (cachedOffensive) {
      setOffensiveRecommendationsData(cachedOffensive);
      setIsLoadingOffensiveRecommendations(false);
    } else {
      setOffensiveRecommendationsData(null);
      setIsLoadingOffensiveRecommendations(true);
    }

    if (cachedDefensive) {
      setDefensiveRecommendationsData(cachedDefensive);
      setIsLoadingDefensiveRecommendations(false);
    } else {
      setDefensiveRecommendationsData(null);
      setIsLoadingDefensiveRecommendations(true);
    }
  }, [matchupConfig, selectedLeague, userTeamMode, getCachedRecommendations, activeReport]);

  // If no cached recommendations are available, trigger fetch when the tab is active
  useEffect(() => {
    if (activeReport !== 'key_insights') return;
    if (!matchupConfig?.opponent_team || !matchupConfig?.user_team || !selectedLeague?.abbreviation || userTeamMode) {
      return;
    }

    const opponentTeamId = matchupConfig.opponent_team.teamId;
    const ourTeamId = matchupConfig.user_team.teamId;

    if (!offensiveRecommendationsData && !isLoadingOffensiveRecommendations) {
      fetchOffensiveRecommendations(opponentTeamId, ourTeamId);
    }

    if (!defensiveRecommendationsData && !isLoadingDefensiveRecommendations) {
      fetchDefensiveRecommendations(opponentTeamId, ourTeamId);
    }
  }, [
    activeReport,
    matchupConfig,
    selectedLeague,
    userTeamMode,
    offensiveRecommendationsData,
    defensiveRecommendationsData,
    isLoadingOffensiveRecommendations,
    isLoadingDefensiveRecommendations,
    fetchOffensiveRecommendations,
    fetchDefensiveRecommendations
  ]);

  // Get team info - show user team in userTeamMode, otherwise show opponent team
  const opponentTeam = matchupConfig?.opponent_team;
  const userTeam = matchupConfig?.user_team;
  const displayTeam = userTeamMode ? userTeam : opponentTeam;
  const displayTeamStats = userTeamMode ? userTeamStats : opponentTeamStats;
  const displayTeamInsights = userTeamMode ? teamInsights : opponentTeamInsights;
  const displayPlayerInsights = userTeamMode ? playerInsights : opponentPlayerInsights;
  const displayLineupsInsights = userTeamMode ? lineupsInsights : opponentLineupsInsights;

  // Calculate total pages and reset scroll position when insights change
  useEffect(() => {
    const updatePages = () => {
      const container = insightsScrollRef.current;
      if (!container) return;
      
      // Calculate card width dynamically based on container width
      const containerWidth = container.clientWidth;
      const gap = 12; // Gap between cards (0.75rem = 12px)
      // Determine cards per page based on screen width
      const isMobile = containerWidth <= 480; // 30rem = 480px
      const cardsPerPage = isMobile ? 1 : 4;
      
      // Calculate card width based on cards per page
      const cardWidth = isMobile 
        ? containerWidth // Full container width on mobile
        : (containerWidth - (3 * gap)) / cardsPerPage; // 4 cards on desktop
      const cardWithGap = isMobile ? cardWidth : cardWidth + gap;
      
      const totalCards = container.querySelectorAll('[data-insight-card]').length;
      const pages = Math.max(1, Math.ceil(totalCards / cardsPerPage));
      setTotalPages(pages);
    };
    
    // Reset scroll and calculate pages when insights type or data changes
    if (insightsScrollRef.current) {
      insightsScrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      setActiveDot(0);
      
      // Wait for cards to render, then calculate
      setTimeout(updatePages, 100);
      
      // Update on resize
      const resizeObserver = new ResizeObserver(() => {
        updatePages();
      });
      resizeObserver.observe(insightsScrollRef.current);
      
      // Also listen to window resize as fallback
      window.addEventListener('resize', updatePages);
      
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', updatePages);
      };
    }
  }, [insightsStatsType, displayTeamInsights, displayPlayerInsights, displayLineupsInsights]);

  // Monitor insights changes for debugging
  useEffect(() => {
  }, [teamInsights, playerInsights, lineupsInsights, opponentTeamInsights, opponentPlayerInsights, opponentLineupsInsights]);

  // Helper functions to transform API response data for table display
  const transformTeamStatsData = (teamStats: any): any[] => {
    if (!teamStats) return [];
    
    const rows: any[] = [];
    // Get the correct team name from context (opponent team or user team depending on mode)
    const fallbackTeamName = displayTeam?.teamName || opponentTeam?.teamName || 'Unknown Team';
    
    // Handle different response structures
    if (Array.isArray(teamStats)) {
      // If it's an array, each item represents a different split
      teamStats.forEach((splitData: any) => {
        if (splitData.split_type && splitData.own) {
          const row = {
            splitKey: splitData.split_type,
            teamId: splitData.team_id,
            teamName: splitData.team_name || fallbackTeamName, // Use team_name from API, fallback to opponent team name
            wins: splitData.wins,
            losses: splitData.losses,
            own: splitData.own // Keep the nested structure intact
          };
          rows.push(row);
        }
      });
    } else if (teamStats.split_type && teamStats.own) {
      // Single split response
      const row = {
        splitKey: teamStats.split_type,
        teamId: teamStats.team_id,
        teamName: teamStats.team_name || fallbackTeamName, // Use team_name from API, fallback to opponent team name
        wins: teamStats.wins,
        losses: teamStats.losses,
        own: teamStats.own // Keep the nested structure intact
      };
      rows.push(row);
    } else if (teamStats.data && Array.isArray(teamStats.data)) {
      // API response with data array
      teamStats.data.forEach((splitData: any) => {
        if (splitData.splitKey && splitData.own) {
          const row = {
            splitKey: splitData.splitKey,
            teamId: splitData.teamId,
            teamName: splitData.teamName || fallbackTeamName, // Use teamName from data, fallback to opponent team name
            wins: splitData.losses, // Swapped - API returns wins as losses
            losses: splitData.wins,  // Swapped - API returns losses as wins
            own: splitData.own // Keep the nested structure intact
          };
          rows.push(row);
        }
      });
    } else if (teamStats.own) {
      // Direct stats object with own property
      const row = {
        splitKey: teamStats.splitKey || 'overall',
        teamId: teamStats.teamId || 'unknown',
        teamName: teamStats.teamName || fallbackTeamName, // Use teamName from stats, fallback to opponent team name
        wins: teamStats.losses, // Swapped - API returns wins as losses
        losses: teamStats.wins,  // Swapped - API returns losses as wins
        own: teamStats.own
      };
      rows.push(row);
    }
    
    return rows;
  };

  const transformPlayerStatsData = (playerStats: any[]): any[] => {
    if (!playerStats || !Array.isArray(playerStats)) return [];
    
    const rows: any[] = [];
    
    playerStats.forEach((player: any) => {
      if (player.player_name && player.own) {
        const row = {
          player_name: player.player_name,
          split: player.split || 'overall',
          own: player.own
        };
        rows.push(row);
      }
    });
    
    return rows;
  };

  const transformLineupStatsData = (lineupStats: any[]): any[] => {
    if (!lineupStats || !Array.isArray(lineupStats)) return [];
    
    // The API returns data in format: { lineup: string, stats_totals: object, breakdown: object }
    // InsightsLineupsStatsTable expects: { lineup, stats_totals/statsTotals, breakdown }
    return lineupStats.map((lineup: any) => {
      // Check if data is in the new API format (with stats_totals and breakdown)
      if (lineup.lineup && (lineup.stats_totals || lineup.breakdown)) {
        return {
          lineup: lineup.lineup || lineup.lineupKey || '-',
          stats_totals: lineup.stats_totals || lineup.statsTotals || {},
          breakdown: lineup.breakdown || {}
        };
      }
      // Fallback for old format (lineupKey and avg)
      if (lineup.lineupKey && lineup.avg) {
        return {
          lineup: lineup.lineupKey,
          stats_totals: lineup.avg || {},
          breakdown: {}
        };
      }
      // If neither format matches, return as-is (might already be in correct format)
      return {
        lineup: lineup.lineup || lineup.lineupKey || '-',
        stats_totals: lineup.stats_totals || lineup.statsTotals || lineup.avg || {},
        breakdown: lineup.breakdown || {}
      };
    });
  };

  // Chat functionality
  const sendChatMessage = useCallback(async (message: string) => {
    if (!message.trim() || isChatLoading) return;
    
    if (!selectedLeague?.abbreviation) {
      setChatError('No league selected. Please select a league first.');
      return;
    }
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: message,
      role: 'user',
      timestamp: new Date()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setIsChatLoading(true);
    setChatError(null);
    
    try {
      // Build message history (excluding current message)
      const history = chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      // Build match_config
      const matchConfig = {
        stage: matchupConfig?.matchup_type || "regular",
        userTeam: {
          teamId: matchupConfig?.user_team?.teamId || '',
          teamName: matchupConfig?.user_team?.teamName || ''
        },
        opponentTeam: {
          teamId: matchupConfig?.opponent_team?.teamId || '',
          teamName: matchupConfig?.opponent_team?.teamName || ''
        },
        seasonId: matchupConfig?.season || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`,
        league: selectedLeague?.abbreviation
      };
      
      // Build ChatInput object
      const chatInput = {
        message,
        history,
        match_config: matchConfig
      };
      
      const response = await apiService.sendChatMessage(chatInput);
      
      if (response.response) {
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: response.response,
          role: 'assistant',
          timestamp: new Date()
        };
        
        setChatMessages(prev => [...prev, assistantMessage]);
      } else {
        setChatError('No response received from server');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setChatError(errorMessage);
    } finally {
      setIsChatLoading(false);
    }
  }, [chatMessages, matchupConfig, isChatLoading]);
  
  const handleChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (chatInput.trim()) {
      sendChatMessage(chatInput.trim());
      setChatInput('');
    }
  };
  
  const clearChat = () => {
    setChatMessages([]);
    setChatError(null);
  };

  // Handle export report
  const handleExportReport = () => {
    console.log('Exporting report...');
    alert('Report export functionality will be implemented here');
  };

  // Expose the export function to parent component
  useImperativeHandle(ref, () => ({
    handleExportReport: () => {
      handleExportReport();
    }
  }));

  return (
    <FullWidthPageContainer>
      <ReportsContainer>
        <MainContent>
        <TitleSection>
          <MainTitle>
            {displayTeam?.teamName ? `${displayTeam.teamName} Scouting Report`  : 'Loading...'}
          </MainTitle>
        </TitleSection>

        {/* Error Display */}
        {statsError && (
          <ErrorMessage>
            Error loading team stats: {statsError}
          </ErrorMessage>
        )}

        <ReportTabs>
          <ReportTab
            $isActive={activeReport === 'insights'}
            onClick={() => handleTabChange('insights')}
            data-track-event="report_tab_click"
            data-track-label="Insights"
            data-track-detail="insights"
          >
            <span className="desktop-text">Insights</span>
            <span className="mobile-text">Insights</span>
          </ReportTab>
          {!userTeamMode && (
            <ReportTab
              $isActive={activeReport === 'key_insights'}
              onClick={() => handleTabChange('key_insights')}
              data-track-event="report_tab_click"
              data-track-label="Recommendations"
              data-track-detail="key_insights"
            >
              <span className="desktop-text">Recommendations</span>
              <span className="mobile-text">Tips</span>
            </ReportTab>
          )}
          {user?.role === 'system_admin' ? (
            <ReportTab
              $isActive={activeReport === 'chat'}
              onClick={() => handleTabChange('chat')}
              data-track-event="report_tab_click"
              data-track-label="Chat"
              data-track-detail="chat"
            >
              <span className="desktop-text">Chat</span>
              <span className="mobile-text">Chat</span>
            </ReportTab>
          ) : null}
        </ReportTabs>

        <TabContentWrapper>
          <TabContent $isVisible={displayReport === 'insights' && !isTransitioning} $isEntering={activeReport === 'insights'}>
            {displayReport === 'insights' && (
              <>
            {/* Insights Tab Controls */}
            <InsightsControls>
              <ControlsLeft>
                <ControlGroup>
                  <ControlLabel>Stats Type</ControlLabel>
                  <Dropdown
                    options={[
                      { value: 'team', label: 'Team' },
                      { value: 'players', label: 'Players' },
                      { value: 'lineups', label: 'Lineups' }
                    ]}
                    value={insightsStatsType}
                    placeholder="Select stats type..."
                    onChange={(value) => setInsightsStatsType(value as 'team' | 'players' | 'lineups')}
                    minWidth="140px"
                  />
                </ControlGroup>
                
                <ControlGroup>
                  <ControlLabel>Splits</ControlLabel>
                  <SplitsMultiSelect data-dropdown ref={splitsDropdownRef}>
                    <SplitsDropdownContainer
                      onClick={() => {
                        if (!isDropdownOpen) {
                          // Calculate direction BEFORE opening
                          const shouldOpenUpward = calculateSplitsDropdownDirection();
                          setSplitsDropdownOpenUpward(shouldOpenUpward);
                        }
                        setIsDropdownOpen(!isDropdownOpen);
                      }}
                    >
                      <SelectedSplitsContainer>
                        {selectedSplits.length > 0 ? (
                          selectedSplits.map(split => (
                          <SplitChip key={split}>
                            {split}
                            <RemoveSplitButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSplits(selectedSplits.filter(s => s !== split));
                                }}
                            >
                              ×
                            </RemoveSplitButton>
                          </SplitChip>
                          ))
                        ) : (
                          <SplitsPlaceholder
                            onClick={(e) => {
                              e.stopPropagation();
                              // Calculate direction BEFORE opening
                              const shouldOpenUpward = calculateSplitsDropdownDirection();
                              setSplitsDropdownOpenUpward(shouldOpenUpward);
                              setIsDropdownOpen(true);
                            }}
                          >
                            Select splits...
                          </SplitsPlaceholder>
                        )}
                      </SelectedSplitsContainer>
                      
                      {selectedSplits.length > 0 && (
                        <ClearAllButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSplits([]);
                          }}
                        >
                          ×
                        </ClearAllButton>
                      )}
                      
                      <DropdownIcon $isOpen={isDropdownOpen}>
                        ▼
                      </DropdownIcon>
                    </SplitsDropdownContainer>
                    
                    <DropdownList $isOpen={isDropdownOpen} $openUpward={splitsDropdownOpenUpward}>
                      {availableSplits.map(split => (
                          <DropdownOption
                            key={split}
                            $isSelected={selectedSplits.includes(split)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectedSplits.includes(split)) {
                                setSelectedSplits(selectedSplits.filter(s => s !== split));
                              } else {
                                setSelectedSplits([...selectedSplits, split]);
                              }
                            }}
                          >
                            {split}
                            {selectedSplits.includes(split) && ' ✓'}
                          </DropdownOption>
                        ))}
                    </DropdownList>
                  </SplitsMultiSelect>
                </ControlGroup>
              </ControlsLeft>
              
            </InsightsControls>

            {/* Key Insights Section - Always shown above stats */}
            <KeyInsightsSection style={{
              border: '1px solid var(--color-border)',
              borderRadius: '1.25rem',
              padding: '1rem',
              paddingBottom: '1rem', /* Ensure bottom padding doesn't override */
              backgroundColor: 'var(--color-surface)',
              marginBottom: '1rem',
              overflow: 'hidden' /* Ensure borders are visible */
            }}>
                <KeyInsightsTitleContainer>
                  <KeyInsightsTitle>Key Insights</KeyInsightsTitle>
                  <InsightsRefreshButton
                    onClick={() => {
                      if (!matchupConfig) return;
                      const splits = selectedSplits.length > 0 ? selectedSplits : undefined;
                      switch (insightsStatsType) {
                        case 'team':
                          fetchTeamInsights(matchupConfig, splits, true);
                          break;
                        case 'players':
                          fetchPlayerInsights(matchupConfig, splits, true);
                          break;
                        case 'lineups':
                          fetchLineupInsights(matchupConfig, splits, true);
                          break;
                      }
                    }}
                    disabled={isInsightsLoading || !matchupConfig}
                    title="Refresh insights (bypasses cache)"
                  >
                    <RefreshIconWrapper $isSpinning={isInsightsLoading}>
                      <FontAwesomeIcon icon={faRotateRight} />
                    </RefreshIconWrapper>
                    Refresh
                  </InsightsRefreshButton>
                </KeyInsightsTitleContainer>

                {isInsightsLoading ? (
                  <AILoadingScreen 
                    texts={[
                      'AI is analyzing patterns',
                      'Processing statistical insights',
                      'Generating intelligent observations',
                      'Calculating performance metrics',
                      'Neural networks are thinking'
                    ]}
                    subtitle="Our AI is crunching the numbers to provide you with actionable insights"
                  />
                ) : insightsError ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-error)' }}>
                    <div style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} /> Configuration Required
                    </div>
                    <div style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                      {insightsError}
                    </div>
                    <div style={{ fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.8 }}>
                      Please configure your teams in the settings to view insights.
                    </div>
                  </div>
                ) : (() => {
                  // Get the correct insights based on stats type
                  let currentInsights = null;
                  switch (insightsStatsType) {
                    case 'team':
                      currentInsights = displayTeamInsights;
                      break;
                    case 'players':
                      currentInsights = displayPlayerInsights;
                      break;
                    case 'lineups':
                      currentInsights = displayLineupsInsights;
                      break;
                  }
                  
                  return currentInsights && currentInsights.insights && currentInsights.insights.length > 0 ? (
                    <>
                      {/* Dynamic Insights based on stats type */}
                      <MetricsScrollContainer>
                        <MetricsRow 
                          ref={insightsScrollRef}
                          onScroll={(e) => {
                            const container = e.currentTarget;
                            const scrollLeft = container.scrollLeft;
                            const containerWidth = container.clientWidth;
                            const gap = 12; // 0.75rem = 12px
                            const isMobile = containerWidth <= 480; // 30rem = 480px
                            const cardsPerPage = isMobile ? 1 : 4;
                            // Calculate card width based on cards per page
                            const cardWidth = isMobile 
                              ? containerWidth // Full width on mobile (gap handled by scroll)
                              : (containerWidth - (3 * gap)) / cardsPerPage; // 4 cards on desktop
                            const cardWithGap = cardWidth + gap;
                            // Each page is exactly cardsPerPage cards
                            const pageWidth = cardsPerPage * cardWithGap;
                            // Calculate which page we're on - use round instead of floor for better accuracy
                            const currentPage = isMobile 
                              ? Math.round(scrollLeft / cardWidth) // One card per page on mobile - scroll by full card width
                              : Math.round(scrollLeft / pageWidth);
                            const clampedPage = Math.max(0, Math.min(currentPage, totalPages - 1));
                            if (clampedPage !== activeDot) {
                              setActiveDot(clampedPage);
                            }
                          }}
                        >
                          {currentInsights.insights.map((insight: any, index: number) => {
                            const insightKey = `${insightsStatsType}_${index}_${insight.id || insight.title || insight.stat_name || index}`;
                            const isRefreshing = refreshingInsights.has(insightKey);
                            // Determine if this is a page start - on mobile, every card is a page start
                            const isMobile = window.innerWidth <= 480;
                            const isPageStart = isMobile ? true : index % 4 === 0;
                            
                            const isCopied = copiedInsight === insightKey;
                            
                            // Parse value to determine if positive/negative for styling
                            const valueStr = insight.value || '0';
                            const trimmedValue = valueStr.trim();

                            // Check if value contains pipe separator for multi-part coloring
                            const hasPipeSeparator = valueStr.includes('|');

                            // For backward compatibility with single values
                            const hasPlusSign = trimmedValue.startsWith('+');
                            const hasMinusSign = trimmedValue.startsWith('-');
                            const numericValue = parseFloat(trimmedValue.replace(/[^0-9.-]/g, '')) || 0;
                            const isPositive = hasPlusSign || (!hasMinusSign && numericValue > 0);
                            const isNegative = hasMinusSign || numericValue < 0;

                            return (
                            <MetricCard
                              key={insight.id || index}
                              data-insight-card
                              $isPageStart={isPageStart}
                            >
                              <CopyButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyInsight(insight, insightKey);
                                }}
                                title="Copy insight"
                                aria-label="Copy insight to clipboard"
                              >
                                <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} />
                              </CopyButton>
                              <MetricStat>{(insight.stat_name || insight.stat || '').replace(/_/g, ' ')}</MetricStat>
                              <MetricValue $isPositive={!hasPipeSeparator && isPositive} $isNegative={!hasPipeSeparator && isNegative}>
                                {hasPipeSeparator ? (
                                  valueStr.split('|').map((part: string, idx: number) => {
                                    const trimmedPart = part.trim();
                                    const partHasPlus = trimmedPart.includes('+');
                                    const partHasMinus = trimmedPart.includes('-');
                                    const partNumeric = parseFloat(trimmedPart.replace(/[^0-9.-]/g, '')) || 0;
                                    const partIsPositive = partHasPlus || (!partHasMinus && partNumeric > 0);
                                    const partIsNegative = partHasMinus || partNumeric < 0;
                                    const partColor = partIsPositive ? 'var(--color-success)' :
                                                      partIsNegative ? 'var(--color-error)' :
                                                      'var(--color-accent-light)';

                                    return (
                                      <React.Fragment key={idx}>
                                        <span style={{ color: partColor }}>{trimmedPart}</span>
                                        {idx < valueStr.split('|').length - 1 && (
                                          <span style={{ color: 'white', margin: '0 0.25rem' }}>|</span>
                                        )}
                                      </React.Fragment>
                                    );
                                  })
                                ) : (
                                  valueStr
                                )}
                              </MetricValue>
                              <MetricRank>{insight.rank || ''}</MetricRank>
                              {insightsStatsType === 'lineups' ? (
                                <MetricBottomSection>
                                  <MetricBottomItem style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                                    {insight.lineup_name || ''}
                                  </MetricBottomItem>
                                </MetricBottomSection>
                              ) : (
                                <MetricBottomSection>
                                  <MetricBottomItem>{insight.overall || ''}</MetricBottomItem>
                                  <MetricBottomItem>{insight.split_a || ''}</MetricBottomItem>
                                  <MetricBottomItem>{insight.split_b || ''}</MetricBottomItem>
                                </MetricBottomSection>
                              )}
                            </MetricCard>
                            );
                          })}
                        </MetricsRow>
                        {totalPages > 1 && (
                          <DotsContainer>
                            <ArrowButton
                              $disabled={activeDot === 0}
                              disabled={activeDot === 0}
                              onClick={() => {
                                if (insightsScrollRef.current && activeDot > 0) {
                                  const container = insightsScrollRef.current;
                                  const containerWidth = container.clientWidth;
                                  const gap = 12; // 0.75rem = 12px
                                  const isMobile = containerWidth <= 480; // 30rem = 480px
                                  const cardsPerPage = isMobile ? 1 : 4;
                                  // Calculate card width based on cards per page
                                  const cardWidth = isMobile 
                                    ? containerWidth // Full width on mobile (gap handled by scroll)
                                    : (containerWidth - (3 * gap)) / cardsPerPage; // 4 cards on desktop
                                  const cardWithGap = cardWidth + gap;
                                  // Each page is exactly cardsPerPage cards
                                  const pageWidth = cardsPerPage * cardWithGap;
                                  // Move exactly 1 page back
                                  const newPage = Math.max(0, activeDot - 1);
                                  const scrollPosition = isMobile 
                                    ? newPage * cardWidth // One card per page on mobile - scroll by full card width
                                    : newPage * pageWidth;
                                  // Update state immediately to prevent race condition
                                  setActiveDot(newPage);
                                  container.scrollTo({
                                    left: scrollPosition,
                                    behavior: 'smooth'
                                  });
                                }
                              }}
                              aria-label="Previous page"
                              type="button"
                            >
                              ‹
                            </ArrowButton>
                            {Array.from({ length: totalPages }).map((_, index) => (
                              <Dot
                                key={index}
                                $isActive={activeDot === index}
                              />
                            ))}
                            <ArrowButton
                              $disabled={activeDot === totalPages - 1}
                              disabled={activeDot === totalPages - 1}
                              onClick={() => {
                                if (insightsScrollRef.current && activeDot < totalPages - 1) {
                                  const container = insightsScrollRef.current;
                                  const containerWidth = container.clientWidth;
                                  const gap = 12; // 0.75rem = 12px
                                  const isMobile = containerWidth <= 480; // 30rem = 480px
                                  const cardsPerPage = isMobile ? 1 : 4;
                                  // Calculate card width based on cards per page
                                  const cardWidth = isMobile 
                                    ? containerWidth // Full width on mobile (gap handled by scroll)
                                    : (containerWidth - (3 * gap)) / cardsPerPage; // 4 cards on desktop
                                  const cardWithGap = cardWidth + gap;
                                  // Each page is exactly cardsPerPage cards
                                  const pageWidth = cardsPerPage * cardWithGap;
                                  // Move exactly 1 page forward
                                  const newPage = Math.min(totalPages - 1, activeDot + 1);
                                  const scrollPosition = isMobile 
                                    ? newPage * cardWidth // One card per page on mobile - scroll by full card width
                                    : newPage * pageWidth;
                                  // Update state immediately to prevent race condition
                                  setActiveDot(newPage);
                                  container.scrollTo({
                                    left: scrollPosition,
                                    behavior: 'smooth'
                                  });
                                }
                              }}
                              aria-label="Next page"
                              type="button"
                            >
                              ›
                            </ArrowButton>
                          </DotsContainer>
                        )}
                      </MetricsScrollContainer>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <div>
                        {displayTeam ? 
                          `No ${insightsStatsType} insights available for ${displayTeam.teamName}.` : 
                          `Loading...` //was: No ${insightsStatsType} insights available. Please select a team.
                        }
                      </div>
                    </div>
                  );
                })()}
              </KeyInsightsSection>

            {/* Stats Section */}
            <OpponentStatsSection>
              <StatsHeader>
                <StatsTitle>
                  {displayTeam?.teamName} Stats ({matchupConfig?.season || 'N/A'} {matchupConfig?.matchup_type ? matchupConfig.matchup_type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : 'Regular'})
                </StatsTitle>
              </StatsHeader>

              <ScrollableContent>
                <StatsTableContainer>
                  {isStatsLoading ? (
                    <LoadingAnimation />
                  ) : statsError ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-error)' }}>
                      <div>Error loading stats: {statsError}</div>
                      <button 
                        onClick={() => {
                          // Trigger re-fetch by updating a dependency
                          setSelectedSplits([...selectedSplits]);
                        }}
                        style={{
                          marginTop: '1rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: 'var(--color-accent)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (() => {
                    // Transform data based on stats type
                    let transformedData: any[] = [];
                    let hasData = false;
                    
                    
                    switch (insightsStatsType) {
                      case 'team':
                        transformedData = transformTeamStatsData(displayTeamStats);
                        hasData = transformedData.length > 0;
                        break;
                      case 'players':
                        // For players, we need to check if displayTeamStats contains player data
                        // Player stats should be an array of players with splits
                        if (Array.isArray(displayTeamStats)) {
                          transformedData = transformPlayerStatsData(displayTeamStats);
                        } else {
                          // If not an array, try to extract from the data structure
                          transformedData = transformPlayerStatsData(displayTeamStats?.data || displayTeamStats);
                        }
                        hasData = transformedData.length > 0;
                        break;
                      case 'lineups':
                        // For lineups, we need to use the actual lineup stats data, not insights
                        // Lineup stats should be an array of lineups with avg data
                        if (Array.isArray(displayTeamStats)) {
                          transformedData = transformLineupStatsData(displayTeamStats);
                        } else {
                          // If not an array, try to extract from the data structure
                          transformedData = transformLineupStatsData(displayTeamStats?.data || displayTeamStats);
                        }
                        hasData = transformedData.length > 0;
                        break;
                    }
                    
                    if (!hasData) {
                      return (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                          <div>No {insightsStatsType} stats available. Please select splits to view data.</div>
                </div>
                      );
                    }
                    
                    // Render appropriate table component
                    switch (insightsStatsType) {
                      case 'team':
                        return (
                          <TeamStatsTable
                            statsType="traditional"
                            teamStatsData={transformedData}
                            loading={isStatsLoading}
                            error={statsError}
                          />
                        );
                      case 'players':
                        return (
                          <PlayerStatsTable
                            statsType="traditional"
                            opponentPlayers={transformedData}
                            isLoadingPlayers={isStatsLoading}
                          />
                        );
                      case 'lineups':
                        return (
                          <InsightsLineupsStatsTable
                            statsType="by_minutes"
                            lineupStatsData={transformedData}
                            loading={isStatsLoading}
                            error={statsError}
                          />
                        );
                      default:
                        return (
                          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                            <div>Invalid stats type selected.</div>
                          </div>
                        );
                    }
                  })()}
                </StatsTableContainer>
              </ScrollableContent>
            </OpponentStatsSection>
              </>
            )}
          </TabContent>

          <TabContent $isVisible={displayReport === 'key_insights' && !isTransitioning} $isEntering={activeReport === 'key_insights'}>
            {displayReport === 'key_insights' && (
              <RecommendationsTabContainer>
                <RecommendationsContent>
                  <div style={{ marginBottom: '2rem' }}>
                    <h1 style={{
                      fontFamily: "'Helvetica', 'Helvetica Neue', Arial, sans-serif",
                      fontSize: '2.5rem',
                      fontWeight: '700',
                      color: '#e81313',
                      margin: '0 0 2rem 0',
                      textAlign: 'center'
                    }}>
                      Game Plan Recommendations
                    </h1>

                    {(isLoadingOffensiveRecommendations || isLoadingDefensiveRecommendations) ? (
                      <AILoadingScreen 
                        texts={[
                          'AI is analyzing offensive strategies',
                          'Processing defensive patterns',
                          'Generating optimal game plans',
                          'Calculating tactical recommendations',
                          'Neural networks are strategizing'
                        ]}
                        subtitle="Our AI is developing personalized recommendations based on team analysis"
                      />
                    ) : insightsError ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#ff6b6b' }}>
                        <div style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
                          <FontAwesomeIcon icon={faExclamationTriangle} /> Configuration Required
                        </div>
                        <div style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                          {insightsError}
                        </div>
                        <div style={{ fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.8 }}>
                          Please configure your teams in the settings to view recommendations.
                        </div>
                      </div>
                    ) : (offensiveRecommendationsData || defensiveRecommendationsData) ? (
                      <BallotsContainer>
                        {/* OFFENSE BALLOT */}
                        <div style={{
                          border: '1px solid var(--color-border)',
                          borderRadius: '16px',
                          padding: '1.5rem',
                          backgroundColor: 'var(--color-surface)',
                          borderColor: 'var(--color-border)'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '1.5rem'
                          }}>
                            <h2 style={{
                              color: 'var(--color-text-primary)',
                              fontSize: '1.5rem',
                              fontWeight: '700',
                              margin: 0,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <FontAwesomeIcon icon={faChartLine} style={{ color: 'var(--color-accent)' }} /> Offense
                            </h2>
                            <RecommendationRefreshButton 
                              onClick={handleRefreshAllOffensive}
                              disabled={isRefreshingOffensive}
                              title="Refresh offensive recommendations"
                            >
                              <RefreshIconWrapper $isSpinning={isRefreshingOffensive}>
                                <FontAwesomeIcon icon={faRotateRight} />
                              </RefreshIconWrapper>
                            </RecommendationRefreshButton>
                          </div>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {offensiveRecommendationsData && offensiveRecommendationsData.recommendations && offensiveRecommendationsData.recommendations.length > 0 ? (
                              <>
                                {offensiveRecommendationsData.recommendations.slice(0, showAllOffensiveRecommendations ? offensiveRecommendationsData.recommendations.length : 1).map((recommendation: any, index: number) => (
                                  <CollapsibleRecommendation 
                                    key={recommendation.id}
                                    recommendation={recommendation}
                                    index={index}
                                    onApprove={handleApproveOffensive}
                                    onDecline={handleDeclineOffensive}
                                    isLoading={loadingRecommendations.has(recommendation.id)}
                                    isApproving={approvingRecommendations.has(recommendation.id)}
                                  />
                                ))}
                                
                                {offensiveRecommendationsData.recommendations.length > 1 && (
                                  <ShowMoreButton
                                    onClick={() => setShowAllOffensiveRecommendations(!showAllOffensiveRecommendations)}
                                  >
                                    {showAllOffensiveRecommendations ? 'Show Less' : `Show More (${offensiveRecommendationsData.recommendations.length - 1} more)`}
                                  </ShowMoreButton>
                                )}
                              </>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--color-text-secondary)' }}>
                                {isLoadingOffensiveRecommendations ? (
                                  <AILoadingScreen 
                                    texts={['AI is analyzing offensive strategies']}
                                    subtitle="Generating offensive recommendations"
                                    showProgress={false}
                                  />
                                ) : 'No offensive recommendations available'}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* DEFENSE BALLOT */}
                        <div style={{
                          border: '1px solid var(--color-border)',
                          borderRadius: '16px',
                          padding: '1.5rem',
                          backgroundColor: 'var(--color-surface)',
                          borderColor: 'var(--color-border)'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '1.5rem'
                          }}>
                            <h2 style={{
                              color: 'var(--color-text-primary)',
                              fontSize: '1.5rem',
                              fontWeight: '700',
                              margin: 0,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <FontAwesomeIcon icon={faShield} style={{ color: 'var(--color-accent)' }} /> Defense
                            </h2>
                            <RecommendationRefreshButton 
                              onClick={handleRefreshAllDefensive}
                              disabled={isRefreshingDefensive}
                              title="Refresh defensive recommendations"
                            >
                              <RefreshIconWrapper $isSpinning={isRefreshingDefensive}>
                                <FontAwesomeIcon icon={faRotateRight} />
                              </RefreshIconWrapper>
                            </RecommendationRefreshButton>
                          </div>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {defensiveRecommendationsData && defensiveRecommendationsData.recommendations && defensiveRecommendationsData.recommendations.length > 0 ? (
                              <>
                                {defensiveRecommendationsData.recommendations.slice(0, showAllDefensiveRecommendations ? defensiveRecommendationsData.recommendations.length : 1).map((recommendation: any, index: number) => (
                                  <CollapsibleRecommendation 
                                    key={recommendation.id}
                                    recommendation={recommendation}
                                    index={index}
                                    onApprove={handleApproveDefensive}
                                    onDecline={handleDeclineDefensive}
                                    isLoading={loadingRecommendations.has(recommendation.id)}
                                    isApproving={approvingRecommendations.has(recommendation.id)}
                                  />
                                ))}
                                
                                {defensiveRecommendationsData.recommendations.length > 1 && (
                                  <ShowMoreButton
                                    onClick={() => setShowAllDefensiveRecommendations(!showAllDefensiveRecommendations)}
                                  >
                                    {showAllDefensiveRecommendations ? 'Show Less' : `Show More (${defensiveRecommendationsData.recommendations.length - 1} more)`}
                                  </ShowMoreButton>
                                )}
                              </>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--color-text-secondary)' }}>
                                {isLoadingDefensiveRecommendations ? (
                                  <AILoadingScreen 
                                    texts={['AI is analyzing defensive strategies']}
                                    subtitle="Generating defensive recommendations"
                                    showProgress={false}
                                  />
                                ) : 'No defensive recommendations available'}
                              </div>
                            )}
                          </div>
                        </div>
                      </BallotsContainer>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <div>No recommendations available. Please select your team.</div>
                      </div>
                    )}
                  </div>
                </RecommendationsContent>
                
                {(user?.role === 'system_admin' || user?.role === 'admin') && (
                  <ChatWrapper>
                    <RecommendationsChat
                      offensiveRecommendations={offensiveRecommendationsData?.recommendations || []}
                      defensiveRecommendations={defensiveRecommendationsData?.recommendations || []}
                      isLoading={isLoadingOffensiveRecommendations || isLoadingDefensiveRecommendations}
                    />
                  </ChatWrapper>
                )}
              </RecommendationsTabContainer>
            )}
        </TabContent>

        <TabContent $isVisible={displayReport === 'chat' && !isTransitioning} $isEntering={activeReport === 'chat'}>
          {displayReport === 'chat' && (
            <ChatTabContainer>
            {/* Chat Header */}
            <ChatHeader>
              <ChatHeaderTitle>
                Ask anything about the upcoming match against {matchupConfig?.opponent_team?.teamName || 'your opponent'}
              </ChatHeaderTitle>
              {chatMessages.length > 0 && (
                <ClearChatButton onClick={clearChat}>
                  Clear Chat
                </ClearChatButton>
              )}
            </ChatHeader>

            {/* Chat Messages */}
            <ChatMessagesContainer>
              {chatMessages.length === 0 ? (
                <ChatEmptyState>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <FontAwesomeIcon icon={faComments} style={{ fontSize: '1.2rem' }} />
                    <span>Start a conversation</span>
                  </div>
                  <div>Ask about strategies, player matchups, or game analysis</div>
                </ChatEmptyState>
              ) : (
                chatMessages.map((message) => (
                  <div
                    key={message.id}
                    style={{
                      marginBottom: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: message.role === 'user' ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <ChatMessageBubble $isUser={message.role === 'user'}>
                      {message.role === 'assistant' ? (
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          components={{
                            // Custom styling for markdown elements
                            p: ({ children }) => <p style={{ margin: '0 0 0.5rem 0', lineHeight: '1.4' }}>{children}</p>,
                            h1: ({ children }) => <h1 style={{ fontSize: '1.2rem', fontWeight: '600', margin: '0 0 0.5rem 0', color: 'var(--color-accent)' }}>{children}</h1>,
                            h2: ({ children }) => <h2 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '0 0 0.5rem 0', color: 'var(--color-accent)' }}>{children}</h2>,
                            h3: ({ children }) => <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: '0 0 0.5rem 0', color: 'var(--color-accent)' }}>{children}</h3>,
                            ul: ({ children }) => <ul style={{ margin: '0 0 0.5rem 0', paddingLeft: '1.2rem' }}>{children}</ul>,
                            ol: ({ children }) => <ol style={{ margin: '0 0 0.5rem 0', paddingLeft: '1.2rem' }}>{children}</ol>,
                            li: ({ children }) => <li style={{ marginBottom: '0.25rem' }}>{children}</li>,
                            strong: ({ children }) => <strong style={{ fontWeight: '600', color: 'var(--color-accent)' }}>{children}</strong>,
                            em: ({ children }) => <em style={{ fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>{children}</em>,
                            code: ({ children }) => <code style={{ 
                              background: 'var(--color-surface-alt)', 
                              padding: '0.2rem 0.4rem', 
                              borderRadius: '4px', 
                              fontSize: '0.85rem',
                              fontFamily: 'monospace'
                            }}>{children}</code>,
                            pre: ({ children }) => <pre style={{ 
                              background: 'var(--color-surface)', 
                              padding: '0.75rem', 
                              borderRadius: '6px', 
                              overflow: 'auto',
                              fontSize: '0.85rem',
                              fontFamily: 'monospace',
                              margin: '0.5rem 0'
                            }}>{children}</pre>,
                            blockquote: ({ children }) => <blockquote style={{ 
                              borderLeft: '3px solid var(--color-accent)', 
                              paddingLeft: '0.75rem', 
                              margin: '0.5rem 0',
                              fontStyle: 'italic',
                              color: 'var(--color-text-secondary)'
                            }}>{children}</blockquote>,
                            table: ({ children }) => <table style={{ 
                              width: '100%', 
                              borderCollapse: 'collapse', 
                              margin: '0.5rem 0',
                              fontSize: '0.9rem'
                            }}>{children}</table>,
                            th: ({ children }) => <th style={{ 
                              border: '1px solid var(--color-border)', 
                              padding: '0.5rem', 
                              background: 'var(--color-surface-alt)',
                              textAlign: 'left',
                              fontWeight: '600'
                            }}>{children}</th>,
                            td: ({ children }) => <td style={{ 
                              border: '1px solid var(--color-border)', 
                              padding: '0.5rem' 
                            }}>{children}</td>
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      ) : (
                        message.content
                      )}
                    </ChatMessageBubble>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--color-text-secondary)',
                      marginTop: '0.25rem',
                      marginLeft: message.role === 'user' ? '0' : '0.5rem',
                      marginRight: message.role === 'user' ? '0.5rem' : '0'
                    }}>
                      {message.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                ))
              )}
              
              {isChatLoading && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.9rem',
                  marginTop: '0.5rem'
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(var(--color-accent-rgb), 0.3)',
                    borderTop: '2px solid var(--color-accent)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  AI is thinking...
                </div>
              )}
              
              {chatError && (
                <div style={{
                  background: 'rgba(var(--color-error-rgb), 0.1)',
                  border: '1px solid rgba(var(--color-error-rgb), 0.3)',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  color: 'var(--color-accent)',
                  fontSize: '0.9rem',
                  marginTop: '0.5rem'
                }}>
                  Error: {chatError}
                </div>
              )}
            </ChatMessagesContainer>

            {/* Chat Input */}
            <ChatInputForm onSubmit={handleChatSubmit}>
              <ChatInputField
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask anything about the game..."
                disabled={isChatLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleChatSubmit(e);
                  }
                }}
              />
              <ChatSendButton
                type="submit"
                disabled={!chatInput.trim() || isChatLoading}
              >
                {isChatLoading ? (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : (
                  '→'
                )}
              </ChatSendButton>
            </ChatInputForm>
            </ChatTabContainer>
            )}
          </TabContent>
        </TabContentWrapper>
      </MainContent>
      </ReportsContainer>
      <DeclineFeedbackModal
        isOpen={declineModalOpen}
        onClose={handleCloseDeclineModal}
        onSubmit={handleSubmitDeclineFeedback}
        recommendationTitle={decliningRecommendationTitle || undefined}
      />
    </FullWidthPageContainer>
  );
  });
